// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
import { resolveDropTarget, handleType, handleFraction, sideAnchor } from "../../../lib/flow/utils.js"

// G11 helper lines: snap distance in FLOW units (screen deltas are divided by
// zoom in updateNodeDrag, so behavior is zoom-independent) and guide padding.
const HELPER_LINE_THRESHOLD = 6
const HELPER_LINE_PADDING = 8

// Live-region copy defaults (spec T21b). Hosts override individual keys via
// the announcements value — values are free text with {token} interpolation
// (a missing key falls back to these, so partial overrides stay complete).
const DEFAULT_ANNOUNCEMENTS = {
  selectionCleared: "Selection cleared",
  selectionSummary: "{nodes} node{nodesPlural} selected",
  selectionSummaryEdges: "{nodes} node{nodesPlural} selected, {edges} edge{edgesPlural} selected",
  connected: "Connected {source} to {target}",
  deleted: "Deleted {count} element{countPlural}",
  added: "Added {label}",
  duplicated: "Duplicated {label}",
  // U18 keyboard move / connect. The canvas server-renders locale-keyed copies
  // on the announcer (data-announce-<key>); these are the last-resort English.
  nodeMoved: "Moved {label} to {x}, {y}",
  connectStarted: "Connecting from {port}. Move to a target port and press Enter; Escape cancels",
  connectRefused: "Maximum connections reached for {port} ({max})"
}

// Main canvas controller — viewport, selection, connecting, keyboard, data API,
// undo/redo, copy/paste, smooth viewport animations, fitView options.
export default class extends Controller {
  static targets = ["pane", "edges", "nodes", "selectionBox", "helperLines", "announcer"]

  static values = {
    minZoom: { type: Number, default: 0.1 },
    maxZoom: { type: Number, default: 2 },
    zoomOnScroll: { type: Boolean, default: true },
    zoomOnDoubleClick: { type: Boolean, default: true },
    zoomOnPinch: { type: Boolean, default: true },
    panOnDrag: { type: Boolean, default: true },
    panOnScroll: { type: Boolean, default: false },
    panOnScrollSpeed: { type: Number, default: 0.5 },
    selectionOnDrag: { type: Boolean, default: false },
    selectionMode: { type: String, default: "full" },
    selectNodesOnDrag: { type: Boolean, default: true },
    snapToGrid: { type: Boolean, default: false },
    snapGrid: { type: Array, default: [15, 15] },
    helperLines: { type: Boolean, default: false },
    fitViewOnInit: { type: Boolean, default: true },
    // Fit-view padding (FIX M): the init-time fitView reads this value so a
    // host can reserve panel space from the graph edge; fitView's own
    // hardcoded default of 50 is unchanged.
    fitViewPadding: { type: Number, default: 50 },
    // Fit-view zoom ceiling (UX audit): the chrome-aware fit path (no-arg
    // fitView + init) clamps to this so a small graph never opens at 150%+;
    // maxZoom (2) still governs user wheel/button zooming.
    fitViewMaxZoom: { type: Number, default: 1 },
    defaultViewport: { type: Object, default: { x: 0, y: 0, zoom: 1 } },
    translateExtent: { type: Array, default: [] },
    nodeExtent: { type: Array, default: [] },
    preventScrolling: { type: Boolean, default: true },
    autoPanOnConnect: { type: Boolean, default: true },
    autoPanOnNodeDrag: { type: Boolean, default: true },
    autoPanSpeed: { type: Number, default: 15 },
    connectionMode: { type: String, default: "strict" },
    connectionRadius: { type: Number, default: 20 },
    connectionLineType: { type: String, default: "bezier" },
    nodesDraggable: { type: Boolean, default: true },
    nodesConnectable: { type: Boolean, default: true },
    elementsSelectable: { type: Boolean, default: true },
    // Canvas-wide read-only (U2): the master interactivity switch the three
    // flags above stack under. true boots the engine non-interactive —
    // dragging, connecting, selection AND every mutating path (delete, paste,
    // cut, duplicate, drop/menu add, fromObject) gate on it; pan/zoom/fit stay
    // live (a read-only canvas is a viewer, not a frozen one). Runtime
    // toggling goes through setInteractive(); the flags stay meaningful on
    // their own for partial setups (e.g. draggable-only false).
    readOnly: { type: Boolean, default: false },
    // markerEnd default reconciled to "arrowclosed" (S1): EdgeComponent
    // (edge.rb) and edge_controller.js both default to the closed arrowhead —
    // the engine's "arrow" was an inert outlier (never projected into the DOM
    // before the _renderEdgeDom full-def projection). canvas_component.rb's
    // CONTROLLER_DEFAULTS entry carries the same value; runtime rendering is
    // governed by THIS default, so drag-created edges match SSR.
    defaultEdgeOptions: { type: Object, default: { type: "bezier", animated: false, markerEnd: "arrowclosed" } },
    // Live-region copy overrides (M1): keys fixed (see DEFAULT_ANNOUNCEMENTS),
    // values free text with {token} interpolation. Same host-override pattern
    // as toolbar_bar_controller's toasts value.
    announcements: { type: Object, default: {} }
  }

  connect() {
    this.viewport = { ...this.defaultViewportValue }
    // Interactivity state (U2): seeded from the read-only value — the runtime
    // switch setInteractive() takes over from here.
    this._interactive = !this.readOnlyValue
    this.isPanning = false
    this.selectionStart = null
    this.selectedNodes = new Set()
    this.selectedEdges = new Set()
    // Highlight (wave 2 G1): selection-independent, presentation-only id
    // sets — see the Highlight section below. Initialized before
    // _loadInitialElements so the registration hook can read them at boot.
    this._highlightedNodes = new Set()
    this._highlightedEdges = new Set()
    this.isConnecting = false
    this.connectingFrom = null
    // U8: host callback `(sourceHandleId) => handleIds` naming the handles a
    // connect drag from that handle may land on (null = no restriction). The
    // result is cached per gesture (keyed by the connectingFrom object).
    this.validTargets = null
    this._validTargetsFor = null
    this._validTargetsCache = null
    this._validTargetMarks = []
    // BP#1: flow-space pointer of the in-flight connection drag (stashed by
    // updateConnecting) — endConnecting reports it on the no-edge-created
    // paths so a host can open a node-creation palette at the drop point.
    this._connectionPointer = null
    this.draggedNode = null
    this.dragOffset = { x: 0, y: 0 }
    // G11 helper lines: alignment-candidate cache built at drag start
    // (see _beginHelperLines); null means "no guides active".
    this._helperCache = null
    this.spaceHeld = false
    this.isSelecting = false
    this._autoPanRaf = null
    this._autoPanPointer = null // latest pointermove coords — the F3 single-loop auto-pan reads these
    this._animRaf = null
    // BP#9: currently applied zoom-band class (null = near/default band) —
    // _applyZoomBand diffs against this so band-stable frames cost zero
    // classList writes.
    this._zoomBand = null

    // movestart/moveend gesture state (see _beginMoveGesture)
    this._moveActive = false
    this._moveEndTimer = null

    // selectionchange dedupe: signature of the last emitted selection, so
    // mutation points that fire on no-op calls (clearSelection on an empty
    // selection, box-select re-walking the same nodes) don't spam listeners.
    this._lastSelectionSignature = JSON.stringify({ nodes: [], edges: [] })

    // Undo/redo
    this._undoStack = []
    this._redoStack = []
    this._maxHistory = 100
    this._dragStartPositions = null
    // _applySnapshot drives removeNode/removeEdge/edgesadd, which would
    // otherwise push (or emit into) fresh undo entries — every _pushUndo call
    // site checks this flag so undo/redo restore stays self-contained.
    this._applyingSnapshot = false
    // Batch undo (beginBatch/endBatch): while >0, mutation sites skip their
    // own _pushUndo — the single snapshot beginBatch recorded covers the
    // whole batch (e.g. delete_middle_node's removeNode + stitch addEdge).
    this._batchDepth = 0

    // Copy/paste
    this._clipboard = []

    // Live-region announcer (spec T21b): selection announcements are debounced
    // through this timer; cleared on disconnect.
    this._announceTimer = null

    // Reconnect gesture state (ui--flow:reconnectstart → pointerup)
    this._reconnectState = null
    // Bound-state for the gesture's document listeners (backlog): both
    // unbind helpers run from the gesture's own end/cancel AND from
    // disconnect() — the flags keep a mid-gesture teardown complete and a
    // double disconnect a clean no-op.
    this._reconnectGestureBound = false
    this._reconnectCompletionBound = false

    // Monotonic suffix for drag-created edge ids (see _completeConnection)
    this._edgeSeq = 0
    // Monotonic suffix for runtime-created node ids (drop, menu, duplicate,
    // paste) — Date.now() alone collides on same-millisecond creation.
    this._nodeSeq = 0

    this._nodes = new Map()
    this._edges = new Map()

    // Element cache (perf): id → element for every node/edge, kept in sync at
    // every mutation point (_loadInitialElements, _renderNodeDom/_renderEdgeDom,
    // controller self-registration, remove*/_deleteSelected/_applySnapshot).
    // Hot paths (move, drag, select, edge endpoint resolution) read the cache
    // with a document.querySelector fallback on miss — the data Maps stay
    // element-free so _snapshot/toObject deep-copies remain safe (a DOM element
    // inside a node def would survive JSON.stringify as `{}` and corrupt
    // snapshots; separate Maps avoid that).
    this._nodeElements = new Map()
    this._edgeElements = new Map()

    this._loadInitialElements()

    // Chrome-insets contract (UX audit): the per-side space the full-width
    // chrome strips (toolbar bar top, status bar bottom) take from the
    // canvas — see _computeChromeInsets. Computed synchronously at connect so
    // the chrome widgets (controls/minimap/panel, which connect after the
    // engine) read real values immediately; recomputed after first layout
    // inside the init rAF (bars can grow once fonts/layout settle), on window
    // resize, and on ui--flow:chromechange announcements from the widgets.
    this._chromeInsets = { top: 0, right: 0, bottom: 0, left: 0 }
    this._chromeRaf = null
    this._computeChromeInsets()

    this._applyTransform()
    this._applyZoomBand() // BP#9: boot band (a defaultViewport under 0.8 must not wait for the first gesture)
    this._bindEvents()

    if (this.fitViewOnInitValue) {
      // Chrome-aware init fit (UX audit): re-measure the chrome strips after
      // first layout, then fit through the insets + fitViewMaxZoom path.
      requestAnimationFrame(() => { this._computeChromeInsets(); this._fitViewChromeAware() })
    }

    this._emit("ready", { viewport: this.viewport })
  }

  disconnect() {
    this._unbindEvents()
    this._unbindReconnectGesture()
    this._unbindReconnectCompletion()
    this._reconnectState = null
    // G11: a mid-drag teardown must not leave guides behind — guides never
    // survive their drag.
    this._endHelperLines()
    if (this._autoPanRaf) { cancelAnimationFrame(this._autoPanRaf); this._autoPanRaf = null }
    this._autoPanPointer = null
    // A mid-flight smooth viewport animation must not survive its controller:
    // its rAF ticks would re-enter a disconnected controller and keep it alive.
    if (this._animRaf) { cancelAnimationFrame(this._animRaf); this._animRaf = null }
    if (this._moveEndTimer) { clearTimeout(this._moveEndTimer); this._moveEndTimer = null }
    if (this._announceTimer) { clearTimeout(this._announceTimer); this._announceTimer = null }
    if (this._chromeRaf) { cancelAnimationFrame(this._chromeRaf); this._chromeRaf = null }
  }

  _emit(name, detail = {}) {
    this.element.dispatchEvent(new CustomEvent(`ui--flow:${name}`, { detail, bubbles: true }))
  }

  // ── Read-only / interactivity (U2) ──────────────────────
  // Canvas-wide interactivity switch. The three long-declared-but-unread
  // flags each gate their own family below (nodesDraggableValue → dragging,
  // nodesConnectableValue → connecting, elementsSelectableValue → selection);
  // the read-only switch gates ALL of it plus the mutating paths the flags do
  // not cover — delete (keyboard, context menu, edge request), paste, cut,
  // duplicate, drop/menu node creation, fromObject. The node/handle
  // controllers read the same state through the outlet so a blocked gesture
  // never arms its document listeners (see pointerDown/_connectable there).
  // Deliberately NOT gated: pan/zoom/fit (viewer interactions), the data APIs
  // (updateNode/updateEdge/removeNode/removeEdge/moveNode — remote sync ops
  // and host reducers must keep working on a read-only canvas), copySelection
  // (non-destructive) and undo/redo.
  get interactive() { return this._interactive !== false }

  setInteractive(interactive) {
    const next = interactive !== false
    if (this._interactive === next) return
    this._interactive = next
    this._emit("interactivitychange", { interactive: next })
  }

  // ── Element cache (perf) ────────────────────────────────
  // id → element for nodes/edges. Controllers register themselves on connect
  // and deregister on disconnect (deregistration only removes the entry when
  // it still points at THAT element, so an id reused across a remove+add can
  // never evict the newer element). Readers below use `_nodeElement`/
  // `_edgeElement` which serve the cache and lazily back-fill it from a
  // document scan on miss — a node/edge created outside the engine still
  // resolves on first access.

  _registerNodeElement(id, el) { if (id && el) { this._nodeElements.set(id, el); this._applyHighlightToNode(id, el) } }
  _deregisterNodeElement(id, el) { if (this._nodeElements.get(id) === el || !el) this._nodeElements.delete(id) }
  _registerEdgeElement(id, el) { if (id && el) { this._edgeElements.set(id, el); this._applyHighlightToEdge(id, el) } }
  _deregisterEdgeElement(id, el) { if (this._edgeElements.get(id) === el || !el) this._edgeElements.delete(id) }

  // Selector-safe value: ids reach the engine from the DOM, runtime defs and
  // remote sync — interpolating them raw into a querySelector throws a
  // SyntaxError on selector metacharacters (`."#\ []`) and would crash the
  // whole sync apply loop. Every site below routes through this helper. All
  // uses sit inside a DOUBLE-QUOTED attribute value `[data-x="<value>"]`, so
  // the CSS.escape fallback only needs to neutralize quotes and backslashes.
  _escapeSelectorValue(value) {
    const raw = `${value ?? ""}`
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(raw)
    return raw.replace(/(["\\])/g, "\\$1")
  }

  _nodeElement(id) {
    let el = this._nodeElements.get(id)
    if (el) {
      if (el.isConnected) return el
      this._nodeElements.delete(id) // stale — element removed outside the engine
    }
    el = this.hasNodesTarget ? this.nodesTarget.querySelector(`[data-ui--flow-node="${this._escapeSelectorValue(id)}"]`) : null
    if (el) this._nodeElements.set(id, el)
    return el
  }
  _edgeElement(id) {
    let el = this._edgeElements.get(id)
    if (el) {
      if (el.isConnected) return el
      this._edgeElements.delete(id)
    }
    // Scoped to THIS canvas (S5): two canvases sharing an edge id must never
    // cross-resolve through a document-level scan.
    el = this.element.querySelector(`[data-edge="${this._escapeSelectorValue(id)}"]`)
    if (el) this._edgeElements.set(id, el)
    return el
  }

  // Live-region announcements (spec T21b): write into the canvas's sr-only
  // aria-live region (announcer target). Clearing first re-triggers the
  // announcement when the same message repeats. Copy resolves through the
  // announcements value (host override) → DEFAULT_ANNOUNCEMENTS fallback;
  // {token} placeholders interpolate from params.
  _announce(key, params = {}) {
    if (!this.hasAnnouncerTarget) return
    // Host override wins, then the server-rendered locale copy, then English.
    const localized = this.announcerTarget.dataset[`announce${key.charAt(0).toUpperCase()}${key.slice(1)}`]
    const template = (this.announcementsValue || {})[key] || localized || DEFAULT_ANNOUNCEMENTS[key] || ""
    this.announcerTarget.textContent = ""
    this.announcerTarget.textContent = template.replace(/\{(\w+)\}/g, (_, token) => `${params[token] ?? ""}`)
  }

  _announceSelection() {
    if (this._announceTimer) clearTimeout(this._announceTimer)
    this._announceTimer = setTimeout(() => {
      this._announceTimer = null
      const nodes = this.selectedNodes.size, edges = this.selectedEdges.size
      if (nodes === 0 && edges === 0) { this._announce("selectionCleared"); return }
      const params = { nodes, nodesPlural: nodes === 1 ? "" : "s", edges, edgesPlural: edges === 1 ? "" : "s" }
      this._announce(edges ? "selectionSummaryEdges" : "selectionSummary", params)
    }, 300)
  }

  // ── Events ──────────────────────────────────────────────

  _bindEvents() {
    this._onWheel = this._handleWheel.bind(this)
    this._onPointerDown = this._handlePointerDown.bind(this)
    this._onPointerMove = this._handlePointerMove.bind(this)
    this._onPointerUp = this._handlePointerUp.bind(this)
    this._onPointerCancel = this._handlePointerCancel.bind(this)
    this._onKeyDown = this._handleKeyDown.bind(this)
    this._onKeyUp = this._handleKeyUp.bind(this)
    this._onContextMenu = this._handleContextMenu.bind(this)
    this._onDblClick = this._handleDblClick.bind(this)
    this._onReconnectStart = this._handleReconnectStart.bind(this)
    this._onConnectGestureEnd = () => this._clearValidTargetMarks()
    this._onPointerEnter = this._handlePointerEnter.bind(this)
    this._onPointerLeave = this._handlePointerLeave.bind(this)
    // Bubbling engine-consumed widget events (G2/G3): the dropzone pane, the
    // context menu and the dnd palette all live INSIDE this element, so their
    // events bubble here. The engine creates/mutates the graph and lets every
    // event continue bubbling unmodified — hosts keep the React Flow
    // onDrop/onContextMenu ownership and correlate the created node through
    // the `nodesadd` event addNode emits synchronously (detail mutation was
    // rejected: it would rewrite payloads pinned by the upstream-parity tests).
    this._onDropCreate = this._handleDropEvent.bind(this)
    this._onMenuDelete = this._handleMenuDelete.bind(this)
    this._onMenuDuplicate = this._handleMenuDuplicate.bind(this)
    this._onMenuAddNode = this._handleMenuAddNode.bind(this)
    // OS-level focus loss mid-Space (alt-tab, Mission Control) never delivers
    // the keyup — without this the canvas keeps spaceHeld + the grab cursor
    // forever.
    this._onWindowBlur = () => { if (this.spaceHeld) { this.spaceHeld = false; this.element.style.cursor = "" } }
    // edge_controller dispatches this for a focused, class-selected edge's
    // Delete key — see _handleEdgeRequestDelete.
    this._onEdgeRequestDelete = this._handleEdgeRequestDelete.bind(this)
    // Chrome-insets triggers (UX audit): the widgets announce connect/
    // disconnect through ui--flow:chromechange (scoped by canvas id, like the
    // outlet selectors); a window resize re-measures the strips (rAF-gated —
    // the directory's one-frame coalescing idiom).
    this._onChromeChange = (event) => {
      const canvasId = event?.detail?.canvasId
      if (canvasId && canvasId !== this.element.id) return
      this._computeChromeInsets()
    }
    this._onWindowResize = () => this._scheduleChromeRecompute()

    this.element.addEventListener("wheel", this._onWheel, { passive: false })
    this.element.addEventListener("pointerdown", this._onPointerDown)
    this.element.addEventListener("pointermove", this._onPointerMove)
    this.element.addEventListener("pointerup", this._onPointerUp)
    // pointercancel (F9): the browser cancels the pointer when an HTML5 drag
    // takes over (dnd palette), on touch→scroll handoff, or on OS interrupts.
    // Without a handler the pan/selection/connect gesture state outlives the
    // pointer and the next mousemove "ghost-pans" with no button held.
    this.element.addEventListener("pointercancel", this._onPointerCancel)
    this.element.addEventListener("contextmenu", this._onContextMenu)
    this.element.addEventListener("dblclick", this._onDblClick)
    this.element.addEventListener("pointerenter", this._onPointerEnter)
    this.element.addEventListener("pointerleave", this._onPointerLeave)
    this.element.addEventListener("ui--flow:drop", this._onDropCreate)
    this.element.addEventListener("ui--flow:delete", this._onMenuDelete)
    this.element.addEventListener("ui--flow:duplicate", this._onMenuDuplicate)
    this.element.addEventListener("ui--flow:addnode", this._onMenuAddNode)
    document.addEventListener("keydown", this._onKeyDown)
    document.addEventListener("keyup", this._onKeyUp)
    document.addEventListener("ui--flow:reconnectstart", this._onReconnectStart)
    this.element.addEventListener("ui--flow:connectionend", this._onConnectGestureEnd)
    this.element.addEventListener("ui--flow:connectioncancel", this._onConnectGestureEnd)
    document.addEventListener("ui--flow:edgerequestdelete", this._onEdgeRequestDelete)
    document.addEventListener("ui--flow:chromechange", this._onChromeChange)
    window.addEventListener("blur", this._onWindowBlur)
    window.addEventListener("resize", this._onWindowResize)
  }

  _unbindEvents() {
    this.element.removeEventListener("wheel", this._onWheel)
    this.element.removeEventListener("pointerdown", this._onPointerDown)
    this.element.removeEventListener("pointermove", this._onPointerMove)
    this.element.removeEventListener("pointerup", this._onPointerUp)
    this.element.removeEventListener("pointercancel", this._onPointerCancel)
    this.element.removeEventListener("contextmenu", this._onContextMenu)
    this.element.removeEventListener("dblclick", this._onDblClick)
    this.element.removeEventListener("pointerenter", this._onPointerEnter)
    this.element.removeEventListener("pointerleave", this._onPointerLeave)
    this.element.removeEventListener("ui--flow:drop", this._onDropCreate)
    this.element.removeEventListener("ui--flow:delete", this._onMenuDelete)
    this.element.removeEventListener("ui--flow:duplicate", this._onMenuDuplicate)
    this.element.removeEventListener("ui--flow:addnode", this._onMenuAddNode)
    document.removeEventListener("keydown", this._onKeyDown)
    document.removeEventListener("keyup", this._onKeyUp)
    document.removeEventListener("ui--flow:reconnectstart", this._onReconnectStart)
    this.element.removeEventListener("ui--flow:connectionend", this._onConnectGestureEnd)
    this.element.removeEventListener("ui--flow:connectioncancel", this._onConnectGestureEnd)
    document.removeEventListener("ui--flow:edgerequestdelete", this._onEdgeRequestDelete)
    document.removeEventListener("ui--flow:chromechange", this._onChromeChange)
    window.removeEventListener("blur", this._onWindowBlur)
    window.removeEventListener("resize", this._onWindowResize)
  }

  _handleWheel(event) {
    if (this.preventScrollingValue) event.preventDefault()
    // Any wheel delta that will pan or zoom (plain zoom, ctrl/meta pinch-zoom,
    // pan-on-scroll) opens/continues the move gesture and re-arms the idle timer.
    if (this.zoomOnScrollValue || this.panOnScrollValue) {
      this._beginMoveGesture()
      this._scheduleMoveEnd()
    }
    if ((event.ctrlKey || event.metaKey) && this.zoomOnScrollValue) {
      const delta = -event.deltaY * 0.001
      const rect = this.element.getBoundingClientRect()
      this.zoomAt(delta, { x: event.clientX - rect.left, y: event.clientY - rect.top })
    } else if (this.panOnScrollValue) {
      this.panBy(-event.deltaX * this.panOnScrollSpeedValue, -event.deltaY * this.panOnScrollSpeedValue)
    } else if (this.zoomOnScrollValue) {
      const delta = -event.deltaY * 0.001
      const rect = this.element.getBoundingClientRect()
      this.zoomAt(delta, { x: event.clientX - rect.left, y: event.clientY - rect.top })
    }
  }

  _handlePointerDown(event) {
    if (event.button === 2) return
    // Canvas chrome (F9): controls, minimap, panels, toolbar/status bars,
    // context menu and the dnd palette all render INSIDE the canvas root
    // (canvas_component marks their shared wrapper with data-flow-chrome).
    // A press on a widget is a UI interaction, not a pane gesture — it must
    // not clear the selection, emit paneclick, or start a pan/box-select.
    if (event.target instanceof Element && event.target.closest("[data-flow-chrome]")) return
    const node = event.target.closest("[data-ui--flow-node]")
    const handle = event.target.closest("[data-handle]")
    const edgeInteraction = event.target.closest("[data-edge-interaction]")
    if (node || handle) return

    if (edgeInteraction) {
      const edgeId = edgeInteraction.dataset.edgeInteraction
      if (!event.shiftKey) this.clearSelection()
      this.selectEdge(edgeId)
      this._emit("edgeclick", { edgeId })
      return
    }

    // paneclick is a PLAIN-press signal: a shift press starts an additive
    // box-select (below), it is not a pane click (React Flow parity).
    if (!event.shiftKey) { this.clearSelection(); this._emit("paneclick", { position: this.screenToFlow(event.clientX, event.clientY) }) }

    // Shift+drag ALWAYS box-selects (React Flow parity): with the default
    // selectionOnDrag=false / panOnDrag=true config, Shift is the ONLY
    // reachable box-select gesture. Space still forces pan. A shift gesture
    // commits additively — the clear above was skipped for shift, and
    // _selectNodesInBox only ever unions into the existing selection.
    if ((this.selectionOnDragValue || event.shiftKey) && !this.spaceHeld) this._startSelection(event)
    else if (this.panOnDragValue) this._startPan(event)
  }

  _handlePointerMove(event) {
    if (this.isPanning) this._doPan(event)
    else if (this.isSelecting) this._doSelection(event)
    else if (this.isConnecting) {
      // A button-held connect drag — handle_controller's pointer path or an
      // edge reconnect — owns a DOCUMENT-level pointermove feed into
      // updateConnecting, so emitting here too double-dispatched
      // connectionmove for the same pointermove (minor #12). The keyboard
      // connect path binds no move feed (buttons === 0 with no drag), so it
      // keeps this element-level tracking. Auto-pan runs either way: a handle
      // drag near the canvas edge must still pan.
      if (!event.buttons) {
        const rect = this.element.getBoundingClientRect()
        const x = (event.clientX - rect.left - this.viewport.x) / this.viewport.zoom
        const y = (event.clientY - rect.top - this.viewport.y) / this.viewport.zoom
        this.updateConnecting(x, y)
      }
      if (this.autoPanOnConnectValue) this._autoPan(event.clientX, event.clientY)
    }
  }

  _handlePointerUp() {
    if (this.isPanning) this._endPan()
    else if (this.isSelecting) this._endSelection()
    this._stopAutoPan()
  }

  // pointercancel mirrors pointerup for every gesture (F9), except that a
  // cancelled connection has no drop target — it ABORTS (connectioncancel)
  // rather than completes. Covers pan, box selection, connect and (via the
  // connectioncancel → reconnect-cancel chain) reconnect.
  _handlePointerCancel() {
    if (this.isPanning) this._endPan()
    if (this.isSelecting) this._endSelection()
    if (this.isConnecting) this._cancelConnecting()
    this._stopAutoPan()
  }

  // Hover (spec gap closure ④): paneenter/paneleave carry the flow-space
  // position under the pointer.
  _handlePointerEnter(event) {
    this._emit("paneenter", { position: this.screenToFlow(event.clientX, event.clientY) })
  }

  _handlePointerLeave(event) {
    this._emit("paneleave", { position: this.screenToFlow(event.clientX, event.clientY) })
  }

  _startPan(event) {
    this.isPanning = true
    this.panStart = { x: event.clientX, y: event.clientY }
    this.panViewportStart = { x: this.viewport.x, y: this.viewport.y }
    this.element.classList.add("dragging-canvas")
    this.element.setPointerCapture(event.pointerId)
    this._beginMoveGesture()
  }
  _doPan(event) {
    this.viewport.x = this.panViewportStart.x + (event.clientX - this.panStart.x)
    this.viewport.y = this.panViewportStart.y + (event.clientY - this.panStart.y)
    this._applyTranslateExtent(); this._applyTransform(); this._emitViewportChange()
  }
  _endPan() { this.isPanning = false; this.element.classList.remove("dragging-canvas"); this._cancelMoveEndTimer(); this._endMoveGesture() }

  _startSelection(event) {
    // Canvas-wide gates (U2): with selection blocked the box must not even
    // appear — _selectNodesInBox routes through the gated selectNode/selectEdge,
    // but a rubber-band that selects nothing is a broken read.
    if (!this.interactive || !this.elementsSelectableValue) return
    this.isSelecting = true
    const rect = this.element.getBoundingClientRect()
    this.selectionStart = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    // Capture the pointer (F9): without it, releasing OUTSIDE the canvas
    // never delivers pointerup here and isSelecting stays stuck on.
    if (event.pointerId != null) {
      try { this.element.setPointerCapture(event.pointerId) } catch (e) { /* synthetic events carry no active pointer */ }
    }
    this._showSelectionBox(this.selectionStart, this.selectionStart)
  }
  _doSelection(event) {
    const rect = this.element.getBoundingClientRect()
    const current = { x: event.clientX - rect.left, y: event.clientY - rect.top }
    this._showSelectionBox(this.selectionStart, current)
    this._selectNodesInBox(this.selectionStart, current)
  }
  _endSelection() { this.isSelecting = false; this._hideSelectionBox() }

  _showSelectionBox(start, end) {
    if (!this.hasSelectionBoxTarget) return
    const box = this.selectionBoxTarget
    // A 0×0 box is a held press, not a region — keep it hidden until the
    // drag has area (a bordered 0×0 box renders as a stray dot).
    if (start.x === end.x && start.y === end.y) { box.classList.add("hidden"); return }
    box.classList.remove("hidden")
    box.style.left = `${Math.min(start.x, end.x)}px`
    box.style.top = `${Math.min(start.y, end.y)}px`
    box.style.width = `${Math.abs(end.x - start.x)}px`
    box.style.height = `${Math.abs(end.y - start.y)}px`
  }
  _hideSelectionBox() { if (this.hasSelectionBoxTarget) this.selectionBoxTarget.classList.add("hidden") }

  // Element-relative → flow-space conversion for the selection box (F1):
  // _startSelection/_doSelection already subtract the canvas rect, so only
  // the viewport transform remains — screenToFlow would subtract the rect a
  // SECOND time, shifting the hit rect by (-rect.left/zoom, -rect.top/zoom)
  // on every page where the canvas isn't at (0,0).
  _elementToFlow(x, y) {
    return { x: (x - this.viewport.x) / this.viewport.zoom, y: (y - this.viewport.y) / this.viewport.zoom }
  }

  // THE node-visibility predicate (backlog): computed style, so a node
  // hidden by a CSS CLASS counts exactly like the engine's own inline
  // display:none hide(). Box-select, fitView and the minimap all read this
  // one helper — the three views of the graph must agree on what is hidden.
  _nodeVisible(el) {
    return window.getComputedStyle(el).display !== "none"
  }

  _selectNodesInBox(start, end) {
    const s = this._elementToFlow(Math.min(start.x, end.x), Math.min(start.y, end.y))
    const e = this._elementToFlow(Math.max(start.x, end.x), Math.max(start.y, end.y))
    // Iterate the engine's data map rather than nodesTarget.querySelectorAll:
    // a box-select pointermove must not rescan the DOM per frame.
    this._nodes.forEach((_, id) => {
      // Membership guard (wave-8 C3 — mirrors the edge branch below): a
      // box-select sweep re-runs per pointermove; re-selecting an already
      // covered node re-did classList/aria-pressed writes and re-dispatched
      // nodeselect + selectionchange EVERY frame (O(K²) over the sweep).
      if (this.selectedNodes.has(id)) return
      const n = this._nodeElement(id)
      if (!n) return
      // Hidden nodes are not selectable (React Flow parity; m6): the shared
      // _nodeVisible predicate (fitView and the minimap read it too) is
      // computed-style based, so class-hidden SSR nodes count too, not just
      // the inline display the engine's own hide() writes.
      if (!this._nodeVisible(n)) return
      if (this.selectionModeValue === "partial") {
        const { x: nx, y: ny } = this.flowPosition(n)
        if (nx < e.x && nx + n.offsetWidth > s.x && ny < e.y && ny + n.offsetHeight > s.y) this.selectNode(id)
      } else {
        // "full" (React Flow parity): the WHOLE node rect must lie inside
        // the box — the old center-only test let half-outside nodes in.
        const { x, y } = this.flowPosition(n)
        if (x >= s.x && y >= s.y && x + n.offsetWidth <= e.x && y + n.offsetHeight <= e.y) this.selectNode(id)
      }
    })
    // Box selection covers edges too (React Flow parity): an edge joins the
    // selection only when BOTH of its endpoints lie inside the rect.
    const inRect = p => p && p.x >= s.x && p.x <= e.x && p.y >= s.y && p.y <= e.y
    this._edges.forEach((edge, id) => {
      if (this.selectedEdges.has(id)) return
      const source = this._edgeEndpoint(edge.source, edge.sourceHandle, "right")
      const target = this._edgeEndpoint(edge.target, edge.targetHandle, "left")
      if (inRect(source) && inRect(target)) this.selectEdge(id)
    })
  }

  // Flow-space endpoint of an edge: the node's handle position — resolved from
  // the handle element's side when available, falling back to the edge's own
  // side (source "right" / target "left", the NodeComponent defaults).
  // Port-qualified endpoint fallback (conek-workflows): the engine addresses
  // endpoints as "node.port" ("gate.vip" — node gate, port vip) with no
  // separate handle value. When the full id misses as a node id AND the id
  // contains "." AND the prefix before the LAST "." resolves as a node, the
  // endpoint resolves to that node and the port suffix becomes the effective
  // handle id — the SAME three-condition split edge_controller
  // ._resolveEndpoint makes (keep the two paths in sync), so box-select
  // anchors a port-qualified edge exactly where the rendered path does.
  _edgeEndpoint(nodeId, handleId, fallbackPosition) {
    let el = this._nodeElement(nodeId)
    if (!el && typeof nodeId === "string" && nodeId.includes(".")) {
      const dot = nodeId.lastIndexOf(".")
      el = this._nodeElement(nodeId.slice(0, dot))
      if (el) handleId = nodeId.slice(dot + 1)
    }
    if (!el) return null
    // Handle lookup is scoped to the endpoint's OWN node first (minor #17):
    // two nodes may carry the same handle id on different sides, and a
    // canvas-wide first-match would anchor the path to the other node's
    // handle. The canvas-wide fallback stays for markup that parks handles
    // outside their node element (legacy fixtures/tests).
    const handleSelector = `[data-handle="${this._escapeSelectorValue(handleId)}"]`
    const handle = handleId ? el.querySelector(handleSelector) || this.element.querySelector(handleSelector) : null
    const position = handle?.dataset.handlePosition || fallbackPosition
    const { x, y } = this.flowPosition(el)
    return sideAnchor(position, x, y, el.offsetWidth, el.offsetHeight, handleFraction(handle))
  }

  _handleDblClick(event) {
    if (event.target.closest("[data-ui--flow-node]")) return
    // Chrome guard (minor #7): the same bail _handlePointerDown has — a
    // double-click on toolbar/panel/minimap chrome is a widget interaction,
    // not a canvas zoom gesture.
    if (event.target instanceof Element && event.target.closest("[data-flow-chrome]")) return
    if (this.zoomOnDoubleClickValue) {
      const rect = this.element.getBoundingClientRect()
      this.zoomAt(0.5, { x: event.clientX - rect.left, y: event.clientY - rect.top })
    }
  }

  // Document-level keydown (same guard as node_controller._onKeydown): a
  // form field inside the host page must never trigger canvas shortcuts —
  // Backspace in an input would otherwise delete selected graph nodes and
  // Ctrl+A/C/V/Z would be preventDefault'ed away from the field.
  // Multi-canvas keyboard ownership (S5): the keydown listener is
  // document-level, so without a gate Delete / Cmd+A / Cmd+Z fire on EVERY
  // connected canvas at once. A shortcut belongs to the canvas containing the
  // event target or, for document-targeted dispatch (synthetic events), the
  // canvas containing the focused element. Focus on <body> (or a synthetic
  // document dispatch with no canvas focus) keeps the historical
  // single-canvas behavior: unscoped, every canvas handles it.
  // The resolution is cached on the event at FIRST handler entry: when the
  // owning canvas's handler removes the target (e.g. Backspace → _deleteSelected),
  // the later handlers on the same event would otherwise see a DETACHED
  // target whose closest() returns null and misread the gesture as theirs.
  _keyboardEventIsOurs(event) {
    if (event.target instanceof Element) {
      // Resolve once and cache on the event — a later handler on the same
      // document-level dispatch must read the ORIGINAL canvas even after the
      // owning canvas's handler detached the target element.
      if (event.__flowCanvasLock === undefined) {
        event.__flowCanvasLock = event.target.closest("[data-controller~='ui--flow']")
      }
      if (event.__flowCanvasLock) return event.__flowCanvasLock === this.element
    }
    // Document-targeted dispatch or focusable target outside any canvas: the
    // focused element decides. body/no-focus stays historical — every canvas.
    const active = document.activeElement
    if (active instanceof Element) {
      const canvas = active.closest("[data-controller~='ui--flow']")
      if (canvas) return canvas === this.element
    }
    return true
  }

  _handleKeyDown(event) {
    // A programmatic document-targeted event (tests, synthetic dispatch) has
    // the Document as its target — only an Element can sit inside a form field.
    // (button included — same list node_controller's guard uses: Space on an
    // in-node <button> activates the BUTTON, it must not prime spaceHeld.)
    if (event.target instanceof Element && event.target.closest("input, textarea, select, button, [contenteditable]")) return
    if (!this._keyboardEventIsOurs(event)) return
    // Space is a global pan modifier (React Flow semantics) — the flag stays
    // live even when a focused node consumed the press as a selection. But the
    // grab cursor is cosmetic: a node-level Space (defaultPrevented — the
    // property survives bubbling) selected, it did not enter pan mode, so
    // don't advertise it.
    if (event.code === "Space" && !this.spaceHeld) { this.spaceHeld = true; if (!event.defaultPrevented) this.element.style.cursor = "grab" }
    // The canvas consumed the deletion key, so the default must not also
    // run. Browsers removed Backspace history-navigation in 2016 (Chrome 52),
    // so outside form fields the default is inert in modern browsers — but
    // preventDefault keeps the deletion atomic against host-level Backspace
    // bindings and embedded-webview navigation leftovers. The form-field
    // guard above already returned, so typing is unaffected.
    if (event.key === "Delete" || event.key === "Backspace") { event.preventDefault(); this._deleteSelected() }
    if (event.key === "Escape") { this.clearSelection(); this._cancelConnecting() }
    if ((event.metaKey || event.ctrlKey) && event.key === "a") { event.preventDefault(); this._selectAll() }
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key === "z") { event.preventDefault(); this.undo() }
    // Shift+Z redo (M6): with Shift held the browser reports key "Z", so a
    // case-sensitive "z" match never fires — compare case-insensitively.
    if ((event.metaKey || event.ctrlKey) && (event.key === "y" || (event.shiftKey && event.key.toLowerCase() === "z"))) { event.preventDefault(); this.redo() }
    if ((event.metaKey || event.ctrlKey) && event.key === "c") { event.preventDefault(); this.copySelection() }
    if ((event.metaKey || event.ctrlKey) && event.key === "v") { event.preventDefault(); this.pasteClipboard() }
    // BP#10 shortcut fill-in (same form-field guard + per-canvas ownership as
    // the bindings above):
    if ((event.metaKey || event.ctrlKey) && event.key === "d") { event.preventDefault(); this._duplicateSelection() }
    // Cut = copy + the same delete path Delete/Backspace run (one undo entry).
    // The clipboard is node-centric (copySelection skips edge-only selections),
    // so an edge-only cut deletes without capturing — undo restores it.
    if ((event.metaKey || event.ctrlKey) && event.key === "x") { event.preventDefault(); this.copySelection(); this._deleteSelected() }
    // Zoom reset mirrors the controls cluster's resetView exactly.
    if ((event.metaKey || event.ctrlKey) && event.key === "0") { event.preventDefault(); this.setViewport({ x: 0, y: 0, zoom: 1 }) }
    // The engine stays search-free: Cmd/Ctrl+F only EMITS `searchrequest` —
    // the host owns the search UI (overrides the browser find on purpose).
    if ((event.metaKey || event.ctrlKey) && event.key === "f") { event.preventDefault(); this._emit("searchrequest") }
    // Plain F = fitView. No modifiers (Shift+F types "F", Alt+F is a menu
    // accelerator); Cmd/Ctrl+F is the search hook above.
    if (!event.metaKey && !event.ctrlKey && !event.altKey && event.key === "f") { event.preventDefault(); this.fitView() }
  }
  // Only the canvas that latched spaceHeld releases it (S5) — an ungated
  // keyup must not clear another canvas's cursor state.
  _handleKeyUp(event) { if (event.code === "Space" && this.spaceHeld) { this.spaceHeld = false; this.element.style.cursor = "" } }

  _handleContextMenu(event) {
    event.preventDefault()
    const node = event.target.closest("[data-ui--flow-node]")
    this._emit("contextmenu", { x: event.clientX, y: event.clientY, nodeId: node?.dataset?.["ui-FlowNode"] || null, flowPosition: this.screenToFlow(event.clientX, event.clientY) })
  }

  // ── Viewport ────────────────────────────────────────────

  panBy(dx, dy) { this.viewport.x += dx; this.viewport.y += dy; this._applyTranslateExtent(); this._applyTransform(); this._emitViewportChange() }

  zoomAt(delta, point) {
    const oldZoom = this.viewport.zoom
    let newZoom = Math.max(this.minZoomValue, Math.min(this.maxZoomValue, oldZoom * (1 + delta)))
    if (newZoom === oldZoom) return
    // Programmatic zoom (controls buttons, dblclick, keyboard) is a one-shot
    // move gesture (minor #19): before, only the wheel path emitted the
    // movestart/moveend pair, so button/dblclick zooms were invisible to
    // gesture listeners. The wheel path opens its own gesture around this
    // call — _moveActive makes the nested _beginMoveGesture a no-op and
    // _scheduleMoveEnd simply re-arms the same idle timer.
    this._beginMoveGesture()
    const ratio = newZoom / oldZoom
    this.viewport.x = point.x - (point.x - this.viewport.x) * ratio
    this.viewport.y = point.y - (point.y - this.viewport.y) * ratio
    this.viewport.zoom = newZoom
    this._applyTransform(); this._emitViewportChange()
    this._scheduleMoveEnd()
  }

  zoomIn()  { const r = this.element.getBoundingClientRect(); this.zoomAt(0.2, { x: r.width / 2, y: r.height / 2 }) }
  zoomOut() { const r = this.element.getBoundingClientRect(); this.zoomAt(-0.2, { x: r.width / 2, y: r.height / 2 }) }

  setCenter(x, y, zoom = 1) {
    const rect = this.element.getBoundingClientRect()
    this.viewport = { x: rect.width / 2 - x * zoom, y: rect.height / 2 - y * zoom, zoom }
    this._applyTransform(); this._emitViewportChange()
  }

  fitView(optionsOrPadding) {
    // LOCKED SEMANTIC (UX audit — do not deviate): chrome insets apply only
    // when the caller passes no options at all. A bare fitView() (and the
    // init path) goes chrome-aware; ANY explicit option — a number, or an
    // object with any padding/paddingTop/…/minZoom/maxZoom/duration key —
    // means the caller takes full control: use exactly what they passed, NO
    // inset addition.
    if (optionsOrPadding === undefined) return this._fitViewChromeAware()
    const options = typeof optionsOrPadding === "number"
      ? { padding: optionsOrPadding }
      : optionsOrPadding
    const { padding = 50, paddingTop, paddingRight, paddingBottom, paddingLeft, minZoom, maxZoom, duration } = options
    // Per-side padding (UX audit): each side falls back to the uniform
    // `padding`, so the single-number form behaves exactly as before.
    const padT = paddingTop ?? padding
    const padR = paddingRight ?? padding
    const padB = paddingBottom ?? padding
    const padL = paddingLeft ?? padding

    if (!this.hasNodesTarget) return
    // Hidden nodes contribute no bounds — the shared _nodeVisible predicate
    // (box-select and the minimap read it too), so the three views of the
    // graph agree: class-hidden nodes count, not just inline display:none.
    const nodes = [...this.nodesTarget.querySelectorAll("[data-ui--flow-node]")].filter(n => this._nodeVisible(n))
    // Wave-8 A6: the non-animated empty-graph reset must emit viewportchange
    // like every other viewport write (the non-empty branch below and
    // setViewportAnimated both do) — the background pattern, minimap rect,
    // status bar and toolbar zoom label listen ONLY for viewportchange and
    // otherwise go stale until the next gesture.
    if (nodes.length === 0) { if (duration) this.setViewportAnimated({ x: 0, y: 0, zoom: 1 }, duration); else { this.viewport = { x: 0, y: 0, zoom: 1 }; this._applyTransform(); this._emitViewportChange() } return }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    nodes.forEach(n => { const { x, y } = this.flowPosition(n); minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x + n.offsetWidth); maxY = Math.max(maxY, y + n.offsetHeight) })
    const rect = this.element.getBoundingClientRect(), cw = maxX - minX + padL + padR, ch = maxY - minY + padT + padB
    const clampMax = maxZoom ?? this.maxZoomValue
    const clampMin = minZoom ?? this.minZoomValue
    const zoom = Math.max(clampMin, Math.min(clampMax, Math.min(rect.width / cw, rect.height / ch)))
    const x = (rect.width - cw * zoom) / 2 - minX * zoom + padL * zoom
    const y = (rect.height - ch * zoom) / 2 - minY * zoom + padT * zoom

    if (duration) {
      this.setViewportAnimated({ x, y, zoom }, duration)
    } else {
      this.viewport = { zoom, x, y }
      this._applyTransform()
      this._emitViewportChange()
    }
  }

  // The ONLY chrome-insets consumer in the engine (UX audit): the no-arg
  // fitView path. Per-side padding = fitViewPaddingValue + that side's chrome
  // inset, so the fit rect clears the toolbar bar (top) and the status bar
  // (bottom); the zoom ceiling is fitViewMaxZoomValue (small graphs open at
  // ≤100% instead of zooming to 2×). Explicit fitView options NEVER come
  // through here — the public method routes them verbatim (see the locked
  // semantic above).
  _fitViewChromeAware() {
    const insets = this.getChromeInsets()
    this.fitView({
      paddingTop: this.fitViewPaddingValue + insets.top,
      paddingRight: this.fitViewPaddingValue + insets.right,
      paddingBottom: this.fitViewPaddingValue + insets.bottom,
      paddingLeft: this.fitViewPaddingValue + insets.left,
      maxZoom: this.fitViewMaxZoomValue
    })
  }

  // Cheap synchronous read of the last measured chrome insets
  // ({top, right, bottom, left}, px) — the widgets (controls/minimap/panel)
  // add these to their 16px offsets.
  getChromeInsets() { return this._chromeInsets || { top: 0, right: 0, bottom: 0, left: 0 } }

  // Chrome-insets contract (UX audit — the one source of truth the controls
  // POSITION_STYLES, the minimap MINIMAP_POSITION_STYLES and the old panel
  // hand-measurement converged on): measure the DIRECT layout children of the
  // [data-flow-chrome] wrapper (display:contents, so its layout children are
  // the canvas root's). A child spanning ≥90% of the canvas width AND
  // touching the top or bottom edge is a full-width strip (the toolbar bar,
  // the status bar) and contributes its extent as that side's inset; floating
  // widgets (controls, minimap, panels) never qualify. Each side clamps to
  // 30% of the canvas so a runaway strip can never swallow the graph. The
  // result is stored on _chromeInsets — getChromeInsets() stays a cheap read
  // and fit-view consumes at call time only.
  _computeChromeInsets() {
    const zero = { top: 0, right: 0, bottom: 0, left: 0 }
    const wrapper = this.element.querySelector("[data-flow-chrome]")
    if (!wrapper) { this._chromeInsets = zero; return }
    const canvasRect = this.element.getBoundingClientRect()
    if (!canvasRect.width || !canvasRect.height) { this._chromeInsets = zero; return }
    const insets = { ...zero }
    for (const child of wrapper.children) {
      const r = child.getBoundingClientRect()
      if (r.width < canvasRect.width * 0.9) continue
      if (r.bottom >= canvasRect.bottom - 2) insets.bottom = Math.max(insets.bottom, canvasRect.bottom - r.top)
      if (r.top <= canvasRect.top + 2) insets.top = Math.max(insets.top, r.bottom - canvasRect.top)
    }
    const clamp = canvasRect.height * 0.3
    insets.top = Math.min(Math.max(insets.top, 0), clamp)
    insets.bottom = Math.min(Math.max(insets.bottom, 0), clamp)
    this._chromeInsets = insets
  }

  // rAF-gated recompute (the directory's one-frame coalescing idiom): a
  // resize storm costs one measure per frame, not one per event.
  _scheduleChromeRecompute() {
    if (this._chromeRaf) return
    this._chromeRaf = requestAnimationFrame(() => {
      this._chromeRaf = null
      this._computeChromeInsets()
    })
  }

  // Empty-state slot (UX audit): SSR renders the [data-ui--flow-target=
  // "emptyState"] div only for an empty graph; runtime 0↔N transitions flip
  // its `hidden` flag here. Membership-based, mirroring the SSR condition
  // (nodes.empty?) — a graph whose nodes are all hidden keeps it hidden.
  _syncEmptyState() {
    const es = this.element.querySelector('[data-ui--flow-target="emptyState"]')
    if (es) es.hidden = this._nodes.size > 0
  }

  setViewport(vp) { Object.assign(this.viewport, vp); this._applyTransform(); this._emitViewportChange() }
  getViewport() { return { ...this.viewport } }

  screenToFlow(x, y) { const r = this.element.getBoundingClientRect(); return { x: (x - r.left - this.viewport.x) / this.viewport.zoom, y: (y - r.top - this.viewport.y) / this.viewport.zoom } }
  flowToScreen(x, y) { const r = this.element.getBoundingClientRect(); return { x: x * this.viewport.zoom + this.viewport.x + r.left, y: y * this.viewport.zoom + this.viewport.y + r.top } }

  // Flow-space position of a node element. Sub-flow children (task 14) nest
  // inside their parent's DOM subtree, so style.left/top are RELATIVE to the
  // parent node — walk the node-ancestor chain summing offsets.
  flowPosition(el) {
    let x = parseFloat(el.style.left) || 0, y = parseFloat(el.style.top) || 0
    let p = el.parentElement
    while (p && p.dataset && p.dataset["ui-FlowNode"]) {
      x += parseFloat(p.style.left) || 0
      y += parseFloat(p.style.top) || 0
      p = p.parentElement
    }
    return { x, y }
  }

  _applyTranslateExtent() {
    if (!this.translateExtentValue || this.translateExtentValue.length < 2) return
    const [[minX, minY], [maxX, maxY]] = this.translateExtentValue
    this.viewport.x = Math.max(minX, Math.min(maxX, this.viewport.x))
    this.viewport.y = Math.max(minY, Math.min(maxY, this.viewport.y))
  }

  // ── Move gesture (movestart/moveend) ────────────────────
  // movestart fires on the first pan/zoom INPUT of a gesture (pointerdown-pan
  // start, first wheel delta — trackpad pinch arrives as ctrl+wheel); moveend
  // on gesture end (pointerup after pan, or 150 ms of wheel idle). Ordering is
  // guaranteed: movestart → viewportchange… → moveend. `_moveActive` guards so
  // repeated wheel events never re-dispatch movestart until a real moveend.

  _beginMoveGesture() {
    if (this._moveActive) return
    this._moveActive = true
    this._emit("movestart", { viewport: { ...this.viewport } })
  }
  _endMoveGesture() {
    if (!this._moveActive) return
    this._moveActive = false
    this._emit("moveend", { viewport: { ...this.viewport } })
  }
  _cancelMoveEndTimer() { if (this._moveEndTimer) { clearTimeout(this._moveEndTimer); this._moveEndTimer = null } }
  _scheduleMoveEnd() {
    this._cancelMoveEndTimer()
    this._moveEndTimer = setTimeout(() => { this._moveEndTimer = null; this._endMoveGesture() }, 150)
  }

  // ── Auto-pan ────────────────────────────────────────────
  // F3: pointermove calls only STORE the latest pointer position; exactly ONE
  // self-rescheduling rAF loop consumes it. The old shape re-entered
  // _autoPan per pointermove and overwrote this._autoPanRaf each time, so N
  // moves spawned N parallel rAF chains — _stopAutoPan cancelled the last
  // one and the rest kept panning forever with stale near-edge coordinates.

  _autoPan(clientX, clientY) {
    this._autoPanPointer = { x: clientX, y: clientY }
    if (this._autoPanRaf) return // the single loop is already running
    this._autoPanRaf = requestAnimationFrame(() => this._autoPanTick())
  }

  _autoPanTick() {
    const p = this._autoPanPointer
    if (!p) { this._autoPanRaf = null; return }
    const rect = this.element.getBoundingClientRect(), threshold = 40, speed = this.autoPanSpeedValue
    let dx = 0, dy = 0
    if (p.x - rect.left < threshold) dx = speed; else if (rect.right - p.x < threshold) dx = -speed
    if (p.y - rect.top < threshold) dy = speed; else if (rect.bottom - p.y < threshold) dy = -speed
    if (dx || dy) {
      this.panBy(dx, dy)
      this._autoPanRaf = requestAnimationFrame(() => this._autoPanTick())
    } else {
      // Pointer left the edge zone — the loop stops; the next _autoPan call
      // (pointermove back near an edge) starts a fresh one.
      this._autoPanRaf = null
    }
  }

  _stopAutoPan() {
    this._autoPanPointer = null
    if (this._autoPanRaf) { cancelAnimationFrame(this._autoPanRaf); this._autoPanRaf = null }
  }

  // ── Selection ───────────────────────────────────────────

  // Per-node selectable flag (spec): a node element carrying
  // data-ui--flow-node-selectable-value="false" is skipped everywhere —
  // clicks, box selection, select-all — mirroring node_controller's own
  // select() guard.
  _nodeSelectable(id) {
    // Cache-friendly: an unresolved element means "no markup opted out" — same
    // as the old querySelector miss semantics.
    const el = this._nodeElement(id)
    return !el || el.dataset["ui-FlowNodeSelectableValue"] !== "false"
  }

  selectNode(id) {
    // Canvas-wide gates (U2): a read-only canvas or elements-selectable false
    // blocks every selection entry point (click, box select, select-all,
    // keyboard) here — the chokepoint they all share.
    if (!this.interactive || !this.elementsSelectableValue) return
    if (!this._nodeSelectable(id)) return
    this.selectedNodes.add(id)
    const el = this._nodeElement(id)
    if (el) { el.classList.add("selected"); el.setAttribute("aria-pressed", "true") }
    this._emit("nodeselect", { nodeId: id })
    this._emitSelectionChange()
  }
  deselectNode(id) {
    this.selectedNodes.delete(id)
    const el = this._nodeElement(id)
    if (el) { el.classList.remove("selected"); el.setAttribute("aria-pressed", "false") }
    this._emit("nodedeselect", { nodeId: id })
    this._emitSelectionChange()
  }
  // Engine-knowledge check for the selection entry points (M2-engine): an
  // edge id is selectable only when the engine can SEE it — the data map, the
  // edge element, or its interaction overlay. The overlay arm is satisfiable
  // by bare host markup with all PERSISTENT knowledge absent (no map entry,
  // no edge element), so selectEdge CAN mint a ghost selectedEdges entry from
  // it — that contract is load-bearing (fixture-backed). The DELETE path is
  // stricter: _deleteSelected requires persistent knowledge (map entry or
  // edge element) and silently purges overlay-only ghosts instead. A
  // stale/typo id has all three absent and must NO-OP — never a throw.
  _edgeKnown(id) {
    if (this._edges.has(id)) return true
    if (this._edgeElement(id)) return true
    return Boolean(this.element.querySelector(`[data-edge-interaction="${this._escapeSelectorValue(id)}"]`))
  }
  selectEdge(id) {
    // Engine-citizen selection (M2-engine): edge_controller.select() delegates
    // here, so this path owns the full contract — selectedEdges membership,
    // the .selected class, edgeselect + selectionchange (which also drives
    // the debounced live-region announcement).
    if (!this.interactive || !this.elementsSelectableValue) return
    if (!this._edgeKnown(id)) return
    const el = this._edgeElement(id)
    if (el && el.dataset["ui-FlowEdgeSelectableValue"] === "false") return
    this.selectedEdges.add(id)
    if (el) { el.classList.add("selected"); this._refreshEdgeMarkerColors(el) }
    this._emit("edgeselect", { edgeId: id })
    this._emitSelectionChange()
  }
  deselectEdge(id) {
    if (!this._edgeKnown(id)) return
    this.selectedEdges.delete(id)
    const el = this._edgeElement(id)
    if (el) { el.classList.remove("selected"); this._refreshEdgeMarkerColors(el) }
    this._emit("edgedeselect", { edgeId: id })
    this._emitSelectionChange()
  }
  // Selection changes the edge stroke (CSS), so the baked arrowhead colors
  // must follow. edge_controller.select()/deselect() do this themselves; the
  // engine-owned selection paths poke the same hook when the edge's
  // controller is mounted (a map-only edge simply has nothing to repaint).
  _refreshEdgeMarkerColors(el) {
    let controller = null
    try { controller = this.application?.getControllerForElementAndIdentifier(el, "ui--flow-edge") || null } catch { controller = null }
    controller?._refreshMarkerColors?.()
  }
  clearSelection() {
    this.selectedNodes.forEach(id => {
      const el = this._nodeElement(id)
      if (el) { el.classList.remove("selected"); el.setAttribute("aria-pressed", "false") }
    })
    // Stale edge visuals: selected edge paths must drop their class too, or a
    // pane click leaves phantom-highlighted edges behind.
    this.selectedEdges.forEach(id => {
      const el = this._edgeElement(id)
      if (!el) return
      el.classList.remove("selected")
      this._refreshEdgeMarkerColors(el)
    })
    this.selectedNodes.clear(); this.selectedEdges.clear()
    this._emitSelectionChange()
  }
  // Unified selection-mutation event (spec §6.3): one selectionchange per
  // ACTUAL change of the selected sets, detail arrays in selection order.
  // No-op when a mutation point fires without changing anything.
  _emitSelectionChange() {
    const detail = { nodes: [...this.selectedNodes], edges: [...this.selectedEdges] }
    const signature = JSON.stringify(detail)
    if (signature === this._lastSelectionSignature) return
    this._lastSelectionSignature = signature
    this._emit("selectionchange", detail)
    this._announceSelection()
  }
  // Select-all covers edges too (React Flow parity) — selectEdge applies the
  // per-edge selectable gate itself.
  _selectAll() { this._nodes.forEach((_, id) => this.selectNode(id)); this._edges.forEach((_, id) => this.selectEdge(id)) }

  // ── Highlight (wave 2 G1) ─────────────────────────────
  // Selection-independent, presentation-only highlight: the host hands over a
  // set of node/edge ids and every OTHER element dims (`.ui-flow-node--dimmed`
  // / `.ui-flow-edge--dimmed` in flow.css) so the set reads at a glance. The
  // primitive the system-map features (search/route/reach/lens) share.
  //
  // Exactly like `selected`, the sets are view chrome: they never enter node/
  // edge defs, so _snapshot/toObject/undo never carry them, and no engine
  // events fire (a highlight is not a data change). The sets are ids only —
  // a removed id simply matches nothing, and a re-registered element (runtime
  // add, undo-restore, controller self-registration) re-reads the live sets
  // through the _registerNodeElement/_registerEdgeElement hook above, so a
  // node added DURING an active highlight lands dimmed.
  //
  // highlight({nodes, edges}) REPLACES the current sets. Unknown ids are
  // ignored (updateNode's absent-def guard idiom). An overall-EMPTY call is a
  // no-op, never a dim-everything — clearing is clearHighlight()'s job, so a
  // search that falls to zero matches cannot blank the canvas by accident.
  highlight({ nodes = [], edges = [] } = {}) {
    const nodeIds = nodes.filter(id => this._nodes.has(id))
    const edgeIds = edges.filter(id => this._edges.has(id))
    if (nodeIds.length === 0 && edgeIds.length === 0) return
    this._highlightedNodes = new Set(nodeIds)
    this._highlightedEdges = new Set(edgeIds)
    this._applyHighlight()
  }

  clearHighlight() {
    if (!this._highlightActive()) return
    this._highlightedNodes.clear()
    this._highlightedEdges.clear()
    this._applyHighlight()
  }

  // Defensive against a registration arriving before connect()'s init (the
  // sets exist by _loadInitialElements in practice — the ?? keeps even a
  // pathological ordering a clean no-op instead of a TypeError).
  _highlightActive() { return (this._highlightedNodes?.size ?? 0) > 0 || (this._highlightedEdges?.size ?? 0) > 0 }

  _applyHighlight() {
    this._nodes.forEach((_, id) => { const el = this._nodeElement(id); if (el) this._applyHighlightToNode(id, el) })
    this._edges.forEach((_, id) => { const el = this._edgeElement(id); if (el) this._applyHighlightToEdge(id, el) })
  }

  // Per-element predicates, shared by _applyHighlight and the registration
  // hooks. classList.toggle(force) is idempotent, so the no-highlight pass
  // over freshly-registered boot markup costs nothing.
  _applyHighlightToNode(id, el) {
    el.classList.toggle("ui-flow-node--dimmed", this._highlightActive() && !this._highlightedNodes.has(id))
  }
  _applyHighlightToEdge(id, el) {
    el.classList.toggle("ui-flow-edge--dimmed", this._highlightActive() && !this._highlightedEdges.has(id))
  }

  // Deletable handles (spec §4.6): removeHandle prunes a connection point;
  // a handle whose deletable value is false is protected — removal is a
  // no-op and handledeleteblocked is emitted instead.
  removeHandle(handleId) {
    const el = this.element.querySelector(`[data-handle="${this._escapeSelectorValue(handleId)}"]`)
    if (!el) return
    if (el.dataset["ui-FlowHandleDeletableValue"] === "false") {
      this._emit("handledeleteblocked", { handleId })
      return
    }
    el.remove()
  }

  // ── Node Drag ───────────────────────────────────────────

  startNodeDrag(nodeId, event) {
    // Canvas-wide gates (U2): a read-only canvas, or nodes-draggable false,
    // blocks dragging — both the pointer path (node_controller checks the
    // same state through the outlet before arming its listeners) and this
    // direct JS-API entry.
    if (!this.interactive || !this.nodesDraggableValue) return
    this._dragStartPositions = this._snapshot()
    this.draggedNode = nodeId
    this.draggedNodeElement = this._nodeElement(nodeId)
    if (!this.draggedNodeElement) return
    this.dragOffset = { x: event.clientX, y: event.clientY, nodeX: parseFloat(this.draggedNodeElement.style.left) || 0, nodeY: parseFloat(this.draggedNodeElement.style.top) || 0 }
    if (!this.selectedNodes.has(nodeId)) { if (!event.shiftKey) this.clearSelection(); this.selectNode(nodeId) }
    // G11 helper lines: alignment candidates are cached ONCE here — a
    // pointermove must never rescan the DOM to find siblings.
    this._beginHelperLines(nodeId)
  }
  // Multi-node drag (perf note): every selected node is written synchronously
  // per pointermove — tests and the public moveNode API pin synchronous
  // style.left/top writes and a synchronous per-node nodemove dispatch, so no
  // rAF accumulation happens at this level. The emit-per-node cost is
  // acceptable because the edge controllers FILTER on detail.nodeId and
  // coalesce their path recomputes behind a per-edge rAF gate; the public
  // movepath stays observable frame-exact.
  updateNodeDrag(event) {
    if (!this.draggedNode) return
    const dx = (event.clientX - this.dragOffset.x) / this.viewport.zoom
    const dy = (event.clientY - this.dragOffset.y) / this.viewport.zoom
    let newX = this.dragOffset.nodeX + dx, newY = this.dragOffset.nodeY + dy
    // G11 helper lines: snapping adjusts BEFORE the position commit so undo
    // entries, the minimap and the status bar all see the snapped value.
    if (this._helperCache) {
      const snapped = this._applyHelperLines(this.draggedNode, newX, newY)
      newX = snapped.x; newY = snapped.y
    }
    this._moveNode(this.draggedNode, newX, newY)
    if (this.draggedNodeElement) {
      // Parent sub-flows (spec §4.1): when the dragged node is an ancestor of a
      // selected node, skip it — the subtree follows via DOM nesting.
      const draggedEl = this.draggedNodeElement
      this.selectedNodes.forEach(id => { if (id === this.draggedNode) return; const el = this._nodeElement(id); if (!el) return; if (draggedEl !== el && draggedEl.contains(el)) return; this._moveNode(id, newX + parseFloat(el.dataset.dragOffsetX || 0), newY + parseFloat(el.dataset.dragOffsetY || 0)) })
    }
    if (this.autoPanOnNodeDragValue) this._autoPan(event.clientX, event.clientY)
  }
  endNodeDrag() {
    if (this.draggedNode) {
      // Only push undo if node position actually changed. Routed through
      // _pushUndo with the PRE-DRAG snapshot (minor #6): the bare stack push
      // this replaced bypassed the snapshot-restore/batch guards and
      // duplicated the history trim inline.
      const current = this._snapshot()
      if (this._dragStartPositions) {
        const prevNode = this._dragStartPositions.nodes.find(n => n.id === this.draggedNode)
        const curNode = current.nodes.find(n => n.id === this.draggedNode)
        if (prevNode && curNode && (prevNode.position.x !== curNode.position.x || prevNode.position.y !== curNode.position.y)) {
          this._pushUndo(this._dragStartPositions)
        }
      }
      this._emit("nodedragend", { nodeId: this.draggedNode })
    }
    this._dragStartPositions = null
    this.draggedNode = null
    this.draggedNodeElement = null
    // G11: clear guides — a guide never survives its drag.
    this._endHelperLines()
    this._stopAutoPan()
  }

  _moveNode(nodeId, x, y) {
    if (this.snapToGridValue) { const [gx, gy] = this.snapGridValue; x = Math.round(x / gx) * gx; y = Math.round(y / gy) * gy }
    if (this.nodeExtentValue?.length >= 2) { const [[nx, ny], [mx, my]] = this.nodeExtentValue; x = Math.max(nx, Math.min(mx, x)); y = Math.max(ny, Math.min(my, y)) }
    const el = this._nodeElement(nodeId); if (!el) return
    // Parent sub-flows (spec §4.1): extent ["parent"] clamps a child inside its
    // parent; expandParent grows the parent first so the child can move there.
    // Unmeasured parents (offsetWidth and offsetHeight both 0 — jsdom, SSR
    // hydration before layout) are left unclamped.
    const parentEl = el.parentElement
    if (parentEl && parentEl.dataset["ui-FlowNode"] && (parentEl.offsetWidth || parentEl.offsetHeight)) {
      // The extent attribute is host-overridable markup — a malformed value
      // must not break dragging, so fall back to no parent clamping.
      let extent = []
      try { extent = JSON.parse(el.dataset["ui-FlowNodeExtentValue"] || "[]") } catch { extent = [] }
      const expand = el.dataset["ui-FlowNodeExpandParentValue"] === "true"
      if (expand) {
        const growW = Math.max(parentEl.offsetWidth, x + el.offsetWidth)
        const growH = Math.max(parentEl.offsetHeight, y + el.offsetHeight)
        if (growW !== parentEl.offsetWidth) parentEl.style.width = `${growW}px`
        if (growH !== parentEl.offsetHeight) parentEl.style.height = `${growH}px`
      }
      if (extent.includes("parent")) {
        x = Math.max(0, Math.min(parentEl.offsetWidth - el.offsetWidth, x))
        y = Math.max(0, Math.min(parentEl.offsetHeight - el.offsetHeight, y))
      }
    }
    // Position contract (perf pass note): style.left/top remains the write
    // side on purpose. The transform translate() switch was evaluated and
    // deferred: node_resizer and layout_controller (owned by other waves)
    // write left/top with left/top transitions and flowPosition readers parse
    // the same pair, so a partial JS-only switch would double-offset or break
    // layout animations. flow.css bakes no left/top defaults — the transform
    // migration lands atomically with the CSS wave; until then batching is
    // carried by the edge-level rAF gate (see edge_controller).
    el.style.left = `${x}px`; el.style.top = `${y}px`
    // F4: keep the Stimulus x/y value attributes in lockstep with the rendered
    // position. node_controller's multi-node drag derives each co-selected
    // node's offset from the DRAGGED node's xValue/yValue (pointerDown), so a
    // value left stale by an earlier drag (or a public moveNode) teleports the
    // co-selected nodes on the next group drag. node_controller defines no
    // xValueChanged/yValueChanged, so the attribute writes fire no callbacks.
    el.setAttribute("data-ui--flow-node-x-value", `${x}`)
    el.setAttribute("data-ui--flow-node-y-value", `${y}`)
    const nd = this._nodes.get(nodeId); if (nd) { nd.position = { x, y } }
    // Edge paths follow via the edge controller's own ui--flow:nodemove
    // listener — the old updateedges dispatch here had zero listeners.
    this._emit("nodemove", { nodeId, x, y })
  }

  // Public move path (T20 sync adapter): remote ops must NOT push undo
  // history — _moveNode mutates position and emits `nodemove` only.
  moveNode(id, x, y) { this._moveNode(id, x, y) }

  // U18 keyboard move: shifts every selected node by (dx, dy) flow px through
  // the same _moveNode the drag uses (snap-to-grid, node extent, parent clamp,
  // edge follow), as ONE undo entry, then announces the result. Honors the
  // canvas gates (read-only / nodes-draggable) and each node's own draggable
  // flag; a node whose ancestor is also selected rides that ancestor's DOM
  // subtree instead of moving twice. Returns true when something moved.
  moveSelectionBy(dx, dy) {
    if (!this.interactive || !this.nodesDraggableValue || (!dx && !dy)) return false
    const els = Array.from(this.selectedNodes)
      .map(id => this._nodeElement(id))
      .filter(el => el && el.dataset["ui-FlowNodeDraggableValue"] !== "false")
    const movable = els.filter(el => !els.some(other => other !== el && other.contains(el)))
    if (!movable.length) return false

    const before = this._snapshot()
    const moved = []
    movable.forEach(el => {
      const id = el.dataset["ui-FlowNode"]
      const x = parseFloat(el.style.left) || 0, y = parseFloat(el.style.top) || 0
      this._moveNode(id, x + dx, y + dy)
      if ((parseFloat(el.style.left) || 0) !== x || (parseFloat(el.style.top) || 0) !== y) moved.push({ id, el })
    })
    if (!moved.length) return false

    this._pushUndo(before)
    moved.forEach(({ id }) => this._emit("nodedragend", { nodeId: id }))
    const last = moved[moved.length - 1].el
    const label = moved.length === 1 ? (last.getAttribute("aria-label") || moved[0].id) : `${moved.length}`
    const pos = this.flowPosition ? this.flowPosition(last) : { x: parseFloat(last.style.left) || 0, y: parseFloat(last.style.top) || 0 }
    this._announce("nodeMoved", { label, x: Math.round(pos.x), y: Math.round(pos.y) })
    return true
  }

  // ── Helper lines (G11) ──────────────────────────────────
  // Dashed sibling-alignment guides while dragging, opt-in via the canvas's
  // helperLines value. The candidate list is cached at drag START — a
  // pointermove must never rescan the DOM (with 500 nodes a per-move
  // querySelectorAll would pay layout per frame); per-move work is arithmetic
  // over the cached boxes plus a ≤2-line svg swap. The overlay lives INSIDE
  // the transformed pane (helperLinesTarget — same contract as the edges and
  // connection-line svgs), so everything below is in FLOW units and inherits
  // pan/zoom; updateNodeDrag divides the screen delta by zoom, making the
  // threshold zoom-independent.

  _beginHelperLines(nodeId) {
    if (!this.helperLinesValue) return
    const draggedEl = this._nodeElement(nodeId)
    if (!draggedEl) return
    // Sibling candidates: every visible node except the dragged one. Sibling
    // boxes are flow-space (flowPosition + layout size — ancestor transforms
    // don't affect offsetWidth/offsetHeight, so both are flow units).
    const xs = [], ys = []
    this._nodes.forEach((_, id) => {
      if (id === nodeId) return
      const el = this._nodeElement(id)
      if (!el || !this._nodeVisible(el)) return
      const pos = this.flowPosition(el)
      const box = { x: pos.x, y: pos.y, w: el.offsetWidth, h: el.offsetHeight }
      xs.push({ v: box.x, box }, { v: box.x + box.w / 2, box }, { v: box.x + box.w, box })
      ys.push({ v: box.y, box }, { v: box.y + box.h / 2, box }, { v: box.y + box.h, box })
    })
    this._helperCache = { xs, ys, w: draggedEl.offsetWidth, h: draggedEl.offsetHeight }
  }

  // Snaps the proposed dragged position onto the nearest sibling
  // left/center/right (x) and top/center/bottom (y) within the threshold and
  // draws the corresponding guide(s). Own anchors come from the dragged
  // node's size cached at drag start — no layout reads per move. Both axes
  // resolve independently (X alignment never forces a Y snap and vice versa).
  _applyHelperLines(nodeId, x, y) {
    const cache = this._helperCache
    if (!cache || !this.hasHelperLinesTarget) return { x, y }
    const { w, h } = cache
    const ownX = [x, x + w / 2, x + w], ownY = [y, y + h / 2, y + h]
    let bx = null, by = null
    for (const c of cache.xs) {
      for (let a = 0; a < 3; a++) {
        const d = c.v - ownX[a], m = Math.abs(d)
        if (m <= HELPER_LINE_THRESHOLD && (!bx || m < bx.m)) bx = { d, m, v: c.v, box: c.box }
      }
    }
    for (const c of cache.ys) {
      for (let a = 0; a < 3; a++) {
        const d = c.v - ownY[a], m = Math.abs(d)
        if (m <= HELPER_LINE_THRESHOLD && (!by || m < by.m)) by = { d, m, v: c.v, box: c.box }
      }
    }
    let nx = x, ny = y
    if (bx) nx = x + bx.d
    if (by) ny = y + by.d

    const svg = this.helperLinesTarget
    svg.replaceChildren()
    if (bx) {
      // Vertical guide from X alignment, spanning both nodes' vertical
      // extents (plus padding).
      this._helperLine(svg, bx.v, Math.min(ny, bx.box.y) - HELPER_LINE_PADDING, bx.v, Math.max(ny + h, bx.box.y + bx.box.h) + HELPER_LINE_PADDING)
      // Snap event on actual correction only — a frame where the node already
      // sits exactly on the guide (delta 0) applies nothing.
      if (bx.d !== 0) this._emit("helpersnap", { nodeId, axis: "x", value: bx.v })
    }
    if (by) {
      // Horizontal guide from Y alignment, spanning both nodes' horizontal
      // extents (plus padding).
      this._helperLine(svg, Math.min(nx, by.box.x) - HELPER_LINE_PADDING, by.v, Math.max(nx + w, by.box.x + by.box.w) + HELPER_LINE_PADDING, by.v)
      if (by.d !== 0) this._emit("helpersnap", { nodeId, axis: "y", value: by.v })
    }
    return { x: nx, y: ny }
  }

  _helperLine(svg, x1, y1, x2, y2) {
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line")
    line.setAttribute("x1", x1)
    line.setAttribute("y1", y1)
    line.setAttribute("x2", x2)
    line.setAttribute("y2", y2)
    svg.appendChild(line)
  }

  // The subsystem owns NO listeners of its own — it rides the engine's
  // existing drag pipeline — so teardown is cache + layer only.
  _endHelperLines() {
    this._helperCache = null
    if (this.hasHelperLinesTarget) this.helperLinesTarget.replaceChildren()
  }

  // ── Connecting ──────────────────────────────────────────

  startConnecting(handleId, nodeId, position) {
    // Canvas-wide gates (U2): a read-only canvas, or nodes-connectable false,
    // blocks connecting — the handle controller checks the same state through
    // the outlet (its _connectable) before arming the gesture, and this gate
    // covers the direct JS-API entry and the keyboard path.
    if (!this.interactive || !this.nodesConnectableValue) return
    this.isConnecting = true; this.connectingFrom = { handleId, nodeId, position }; this._connectionPointer = null; this.element.classList.add("connecting"); this._emit("connectionstart", this.connectingFrom)
    this._markValidTargets()
  }
  updateConnecting(x, y) { if (!this.isConnecting) return; this._connectionPointer = { x, y }; this._emit("connectionmove", { from: this.connectingFrom, x, y }) }

  endConnecting(targetHandleId, targetNodeId) {
    if (!this.isConnecting) return
    const from = this.connectingFrom
    // BP#1 palette contract: a drop that creates NO edge (strict-mode veto or
    // empty space) reports the from-handle plus the flow-space drop point, so
    // a host can open a node-creation palette at the cursor. Resolved drops
    // keep the historical empty detail (the `connect` event carries the pair).
    const dropDetail = {
      handleId: from.handleId,
      nodeId: from.nodeId,
      position: from.position,
      x: this._connectionPointer?.x ?? null,
      y: this._connectionPointer?.y ?? null
    }
    this._connectionPointer = null
    let resolved = false
    if (targetHandleId && targetNodeId && targetNodeId !== from.nodeId) {
      if (this._reconnectState) {
        // Reconnect gesture: record the drop as the edge's new endpoint —
        // connectionend completes the rewire via _completeReconnect. No NEW
        // edge may be created (the gesture mutates the existing one).
        if (this._dropTypeAllowed(targetHandleId, targetNodeId) && this._reconnectAccepted(targetNodeId, targetHandleId)) {
          this._reconnectState.target = { node: targetNodeId, handle: targetHandleId }
          resolved = true
        } else {
          // Wrong-type DIRECT hit (minor #8): resolveDropTarget's radius scan
          // already excludes wrong-type handles, but a drop landing exactly on
          // one resolves it — veto as connectioncancel, symmetric with the
          // fresh-connect veto below, instead of a silent empty connectionend.
          // The reconnect-cancel listener clears the gesture state and the
          // edge is left unchanged.
          this.isConnecting = false; this.connectingFrom = null
          this.element.classList.remove("connecting"); this._emit("connectioncancel", dropDetail); this._stopAutoPan()
          return
        }
      } else if (this._dropTypeAllowed(targetHandleId, targetNodeId) && !this._handleAtCapacity(from.handleId, from.nodeId) && this._connectionAccepted(from.nodeId, from.handleId, targetNodeId, targetHandleId)) {
        this._completeConnection({ sourceNode: from.nodeId, sourceHandle: from.handleId, targetNode: targetNodeId, targetHandle: targetHandleId })
        resolved = true
      } else {
        // Veto: a same-type drop in strict mode (connectionMode defaults to
        // "strict" — source may only land on target and vice versa), a drop
        // outside the host's validTargets set, or a host-canceled
        // beforeconnect is a cancel, not a connection. The veto carries the
        // palette detail — the user DID aim at a spot, the engine merely
        // refused the pair.
        this._announceCapacityRefusal(from, targetHandleId, targetNodeId)
        this.isConnecting = false; this.connectingFrom = null
        this.element.classList.remove("connecting"); this._emit("connectioncancel", dropDetail); this._stopAutoPan()
        return
      }
    }
    this.isConnecting = false; this.connectingFrom = null
    this.element.classList.remove("connecting"); this._emit("connectionend", resolved ? {} : dropDetail); this._stopAutoPan()
  }

  // A drop refused because an end is full says so (live region), so a
  // keyboard or screen-reader user is not left guessing why nothing happened.
  _announceCapacityRefusal(from, targetHandleId, targetNodeId) {
    const full = this._handleAtCapacity(from.handleId, from.nodeId) ? [from.handleId, from.nodeId]
      : this._handleAtCapacity(targetHandleId, targetNodeId) ? [targetHandleId, targetNodeId] : null
    if (!full) return
    const max = this._handleElement(full[0], full[1])?.dataset.handleMaxConnections
    this._announce("connectRefused", { port: this._portName(full[0], full[1]), max })
  }

  // The handle type a drop must carry right now (strict connectionMode only):
  // a fresh drag seeks the OPPOSITE type, a reconnect endpoint re-lands on
  // the SAME type. null = unrestricted (loose mode, or the dragged handle's
  // type is undeclared).
  _validDropType() {
    if (this.connectionModeValue !== "strict" || !this.isConnecting) return null
    const fromType = this._handleTypeById(this.connectingFrom?.handleId)
    if (!fromType) return null
    return this._reconnectState ? fromType : (fromType === "source" ? "target" : "source")
  }

  // Drop gate for a candidate handle: the host's validTargets set (U8), then
  // the strict-mode type gate. Unknown types stay permissive — a veto may only fire on a KNOWN mismatch, so markup that
  // predates type emission never loses connections.
  _dropTypeAllowed(targetHandleId, targetNodeId = null) {
    if (!targetHandleId) return true
    // U9: a handle already holding its max_connections refuses more edges. A
    // reconnect ignores the edge being moved, so re-landing on its own handle
    // is not counted against the cap.
    if (this._handleAtCapacity(targetHandleId, targetNodeId, this._reconnectState?.edgeId)) return false
    // U8: the host's validTargets set restricts on top of the type gate.
    const allowed = this._validTargetSet()
    if (allowed && !allowed.has(targetHandleId)) return false
    const need = this._validDropType()
    if (!need) return true
    const type = this._handleTypeById(targetHandleId)
    return !type || type === need
  }

  // U9 capacity: how many edges attach to a handle, against its
  // max_connections (data-handle-max-connections; absent = unlimited). Handles
  // are matched by node + handle id when the node is known (handle ids are
  // unique per node), else by handle id alone. Gates user gestures only — the
  // data plane (addEdge, fromObject, sync ops) is deliberately not capped.
  _handleAtCapacity(handleId, nodeId = null, ignoreEdgeId = null) {
    const el = this._handleElement(handleId, nodeId)
    const max = parseInt(el?.dataset.handleMaxConnections, 10)
    if (!Number.isFinite(max) || max < 1) return false
    const owner = nodeId ?? el.dataset.handleNode
    let count = 0
    this._edges.forEach((edge, id) => {
      if (id === ignoreEdgeId) return
      if ((edge.source === owner && edge.sourceHandle === handleId) || (edge.target === owner && edge.targetHandle === handleId)) count++
    })
    return count >= max
  }

  _handleElement(handleId, nodeId = null) {
    const escaped = this._escapeSelectorValue(handleId)
    if (nodeId) {
      const scoped = this.element.querySelector(`[data-handle="${escaped}"][data-handle-node="${this._escapeSelectorValue(nodeId)}"]`)
      if (scoped) return scoped
    }
    return this.element.querySelector(`[data-handle="${escaped}"]`)
  }

  // Name used in port announcements: the handle's label, else its id.
  _portName(handleId, nodeId = null) {
    const el = this._handleElement(handleId, nodeId)
    return el?.dataset.handleLabel || handleId
  }

  // U8 valid-target gate. `validTargets(sourceHandleId)` is the host's
  // callback (a plain property on this controller); it returns the handle ids
  // a drag from that handle may land on. Evaluated ONCE per gesture and
  // cached, so hover, highlight and drop agree. A missing callback, a throw
  // or a non-array/Set return means "no restriction" — a host bug must never
  // make every drop fail silently. Returns a Set of handle ids, or null.
  _validTargetSet() {
    const from = this.connectingFrom
    if (!this.isConnecting || !from || typeof this.validTargets !== "function") return null
    if (this._validTargetsFor !== from) {
      this._validTargetsFor = from
      let ids = null
      try { ids = this.validTargets(this._connectAnchorHandle(from)) } catch (error) { console.warn("[ui--flow] validTargets threw; treating the connect as unrestricted", error) }
      this._validTargetsCache = Array.isArray(ids) || ids instanceof Set ? new Set(ids) : null
    }
    return this._validTargetsCache
  }

  // The handle the host's rule is asked about: the drag origin for a fresh
  // connect, the FIXED end of the edge for a reconnect (the moved end is the
  // one being re-landed, so the partner it must accept is the other one).
  _connectAnchorHandle(from) {
    const edge = this._reconnectState && this._edges.get(this._reconnectState.edgeId)
    if (!edge) return from.handleId
    return this._reconnectState.type === "source" ? edge.targetHandle : edge.sourceHandle
  }

  // Predicate form of the valid-target set for the drop-radius scan
  // (resolveDropTarget's `accept`); null when the host restricts nothing.
  _validTargetAccept() {
    const allowed = this._validTargetSet()
    return allowed ? (handleId) => allowed.has(handleId) : null
  }

  // Marks every allowed handle .valid-target for the whole drag (not only on
  // hover), so the host's rule is visible before the pointer moves.
  _markValidTargets() {
    const allowed = this._validTargetSet()
    if (!allowed) return
    allowed.forEach(id => {
      const el = this.element.querySelector(`[data-handle="${this._escapeSelectorValue(id)}"]`)
      if (!el) return
      el.classList.add("valid-target")
      this._validTargetMarks.push(el)
    })
  }

  _clearValidTargetMarks() {
    this._validTargetMarks.forEach(el => el.classList.remove("valid-target"))
    this._validTargetMarks = []
  }

  // U8: a fresh connection asks the host FIRST — a cancelable
  // `ui--flow:beforeconnect` dispatched on the canvas element. Duplicates are
  // vetoed silently by _completeConnection and never bother the host.
  _connectionAccepted(source, sourceHandle, target, targetHandle) {
    if (this._isDuplicateConnection(source, sourceHandle, target, targetHandle)) return true
    return this._dispatchBeforeConnect({ source, sourceHandle, target, targetHandle })
  }

  // Reconnect drop: the same event with the post-drop endpoints (the moved
  // end takes the drop, the other end stays on the edge). detail.edgeId names
  // the edge being rewired.
  _reconnectAccepted(targetNodeId, targetHandleId) {
    const state = this._reconnectState
    const edge = state && this._edges.get(state.edgeId)
    if (!edge) return true
    const moved = state.type === "source"
    const detail = {
      source: moved ? targetNodeId : edge.source,
      sourceHandle: moved ? targetHandleId : edge.sourceHandle,
      target: moved ? edge.target : targetNodeId,
      targetHandle: moved ? edge.targetHandle : targetHandleId,
      edgeId: edge.id
    }
    // Self-loops and duplicates are vetoed by _completeReconnect itself.
    if (detail.source === detail.target || this._isDuplicateConnection(detail.source, detail.sourceHandle, detail.target, detail.targetHandle, edge.id)) return true
    return this._dispatchBeforeConnect(detail)
  }

  _dispatchBeforeConnect(detail) {
    const event = new CustomEvent("ui--flow:beforeconnect", { detail, bubbles: true, cancelable: true })
    this.element.dispatchEvent(event)
    return !event.defaultPrevented
  }

  // Declared type of a handle id: the handle element's own declaration,
  // else the owning node def's captured handle list, else null (unknown).
  _handleTypeById(handleId) {
    if (!handleId) return null
    const el = this.element.querySelector(`[data-handle="${this._escapeSelectorValue(handleId)}"]`)
    if (el) return handleType(el)
    for (const node of this._nodes.values()) {
      const handle = (node.handles || []).find(h => h.id === handleId)
      if (handle) return handle.type || null
    }
    return null
  }

  _cancelConnecting() { if (this.isConnecting) { this.isConnecting = false; this.connectingFrom = null; this.element.classList.remove("connecting"); this._emit("connectioncancel"); this._stopAutoPan() } }

  // Duplicate-veto invariant (React Flow parity): the same source id + source
  // handle + target id + target handle pair must never appear twice. A
  // handle-LESS edge (a predeclared node-pair edge with both handles null)
  // acts as a wildcard: it blocks ANY handle-ful duplicate of the same pair,
  // and a handle-less attempt is blocked by any existing edge on the pair.
  _isDuplicateConnection(sourceNode, sourceHandle, targetNode, targetHandle, ignoreEdgeId = null) {
    return [...this._edges.values()].some(e => {
      if (e.id === ignoreEdgeId || e.source !== sourceNode || e.target !== targetNode) return false
      if (!e.sourceHandle && !e.targetHandle) return true
      if (!sourceHandle && !targetHandle) return true
      return (e.sourceHandle || "") === (sourceHandle || "") &&
        (e.targetHandle || "") === (targetHandle || "")
    })
  }

  // Connection completion (React Flow parity): a successful drop creates the
  // edge in the engine's own map — the drag-to-connect result is undo-tracked
  // and rendered like any other addEdge. Duplicate pairs are silently vetoed
  // (no events, no edge). The `connect` event keeps its legacy detail shape —
  // the sync adapter broadcasts it as the addEdge op.
  _completeConnection(connection) {
    if (this._isDuplicateConnection(connection.sourceNode, connection.sourceHandle, connection.targetNode, connection.targetHandle)) return

    this._pushUndo()
    const edge = {
      id: `connection-${Date.now()}-${this._edgeSeq++}`,
      source: connection.sourceNode,
      sourceHandle: connection.sourceHandle,
      target: connection.targetNode,
      targetHandle: connection.targetHandle,
      ...this.defaultEdgeOptionsValue
    }
    this._edges.set(edge.id, edge)
    this._renderEdgeDom(edge)
    this._emit("edgesadd", { edges: [edge] })
    this._emit("connect", connection)
    this._announce("connected", { source: connection.sourceNode, target: connection.targetNode })
  }

  // ── Edge Reconnect ──────────────────────────────────────
  // A reconnect drag starts from an edge endpoint (ui--flow:reconnectstart,
  // dispatched by the edge controller's updater handles) and must complete on
  // pointerup through the SAME completion path as a fresh connection: the
  // drop resolves a [data-handle], endConnecting finishes the gesture (the
  // edge's endpoint is recorded from the drop), and connectionend drives
  // _completeReconnect — which re-wires the edge, syncs its identity
  // attributes and redraws the path. Document-level move/up mirror
  // handle_controller's gesture so the drag also tracks and ends outside the
  // canvas.

  _handleReconnectStart(event) {
    // Multi-canvas (S5): reconnectstart bubbles up to document from the edge
    // element, so its target sits inside the ORIGINATING canvas — a gesture
    // from another canvas must never arm this engine's reconnect state.
    // (Document-targeted synthetic dispatches carry no Element target and
    // keep the historical unscoped behavior.)
    if (event.target instanceof Element && !this.element.contains(event.target)) return
    if (this.isConnecting) return // Already connecting
    // Defensive (backlog): a re-arm must start from ZERO bound listeners —
    // the closures below are reassigned, so a trio a previous gesture left
    // bound would become unreferenced and unremovable (the stale pointerup
    // would then strip the NEW trio, double-feeding connectionmove on every
    // later gesture). No current cancel path leaves one — Escape, pointercancel
    // and disconnect all unbind — but the flag makes the invariant cheap here.
    if (this._reconnectGestureBound) this._unbindReconnectGesture()
    const { edgeId, type, currentNode, currentHandle } = event.detail
    this._reconnectState = { edgeId, type, target: null } // 'source' or 'target'

    this.startConnecting(currentHandle, currentNode, this._getHandlePosition(currentHandle))

    this._onReconnectMove = (e) => {
      if (!this.isConnecting) return
      const rect = this.element.getBoundingClientRect()
      this.updateConnecting((e.clientX - rect.left - this.viewport.x) / this.viewport.zoom, (e.clientY - rect.top - this.viewport.y) / this.viewport.zoom)
    }
    // The drop resolves a [data-handle] the same way a handle drag does —
    // direct hit, then connectionRadius snap (React Flow parity: a near-miss
    // drop rewires instead of silently aborting) — excluding the handle this
    // endpoint is being dragged AWAY from. endConnecting records the new
    // endpoint and routes completion through connectionend →
    // _completeReconnect.
    this._onReconnectPointerUp = (e) => {
      this._unbindReconnectGesture()
      const originId = this.connectingFrom?.handleId
      const origin = originId ? this.element.querySelector(`[data-handle="${this._escapeSelectorValue(originId)}"]`) : null
      const drop = resolveDropTarget(this, e, origin, this._validDropType(), this._validTargetAccept())
      if (drop && drop !== origin) this.endConnecting(drop.dataset.handle, drop.dataset.handleNode)
      else this.endConnecting()
    }
    this._onReconnectGestureEnd = (e) => {
      if (e.target !== this.element) return // Another canvas's gesture
      this._unbindReconnectCompletion()
      this._completeReconnect()
    }
    this._onReconnectGestureCancel = (e) => {
      if (e.target !== this.element) return // Another canvas's gesture
      // Escape reaches here through the keydown path (→ _cancelConnecting →
      // connectioncancel) with NO pointer event, so the gesture trio is still
      // bound — strip it first, mirroring _onReconnectPointerCancel. A trio
      // left bound outlives the state cleanup, and the next
      // _handleReconnectStart would reassign the closure references and
      // strand the OLD listeners (registered, unreferenced, unremovable).
      this._unbindReconnectGesture()
      this._unbindReconnectCompletion()
      this._reconnectState = null
    }
    // pointercancel (F9): abort the reconnect drag the same way Escape does —
    // _cancelConnecting emits connectioncancel, which _onReconnectGestureCancel
    // turns into the state cleanup above.
    this._onReconnectPointerCancel = () => {
      this._unbindReconnectGesture()
      this._cancelConnecting()
    }

    document.addEventListener("pointermove", this._onReconnectMove)
    document.addEventListener("pointerup", this._onReconnectPointerUp)
    document.addEventListener("pointercancel", this._onReconnectPointerCancel)
    this._reconnectGestureBound = true
    this._bindReconnectCompletion()
  }

  _bindReconnectCompletion() {
    document.addEventListener("ui--flow:connectionend", this._onReconnectGestureEnd)
    document.addEventListener("ui--flow:connectioncancel", this._onReconnectGestureCancel)
    this._reconnectCompletionBound = true
  }

  _unbindReconnectCompletion() {
    // Flag-guarded (backlog): a gesture end racing a controller teardown —
    // or a second disconnect() — must be a no-op, never strip listeners a
    // NEW gesture armed after the first unbind ran.
    if (!this._reconnectCompletionBound) return
    document.removeEventListener("ui--flow:connectionend", this._onReconnectGestureEnd)
    document.removeEventListener("ui--flow:connectioncancel", this._onReconnectGestureCancel)
    this._reconnectCompletionBound = false
  }

  _unbindReconnectGesture() {
    if (!this._reconnectGestureBound) return
    document.removeEventListener("pointermove", this._onReconnectMove)
    document.removeEventListener("pointerup", this._onReconnectPointerUp)
    document.removeEventListener("pointercancel", this._onReconnectPointerCancel)
    this._reconnectGestureBound = false
  }

  _getHandlePosition(handleId) {
    // Find the handle element and return its position — scoped to THIS canvas
    // (S5): two canvases sharing a handle id must not cross-resolve.
    const handle = this.element.querySelector(`[data-handle="${this._escapeSelectorValue(handleId)}"]`)
    if (!handle) return "right"
    return handle.dataset["ui-FlowHandlePositionValue"] || "right"
  }

  _completeReconnect() {
    const state = this._reconnectState
    this._reconnectState = null
    if (!state) return // No gesture in flight

    const edge = this._edges.get(state.edgeId)
    if (!edge || !state.target) return // Dropped on empty space — edge unchanged

    const source = state.type === "source" ? state.target.node : edge.source
    const sourceHandle = state.type === "source" ? state.target.handle : edge.sourceHandle
    const target = state.type === "target" ? state.target.node : edge.target
    const targetHandle = state.type === "target" ? state.target.handle : edge.targetHandle
    if (source === target) return // Self-loop — edge unchanged
    if (this._isDuplicateConnection(source, sourceHandle, target, targetHandle, state.edgeId)) return // Duplicate pair — vetoed

    this._pushUndo()
    // The rewire rides updateEdge (protocol): its attribute projection covers
    // everything a hand-synced DOM block would do — source/target writes,
    // handle set-OR-REMOVE (a null/absent handle retracts the attribute),
    // controller refresh() — and its edgechange emission is what the sync
    // channel composes into a full-def updateEdge op, so a live rewire
    // reaches peers now instead of at the next full sync (a direct map write
    // broadcast nothing). updateEdge pushes no undo of its own — the push
    // above stays the gesture's single entry.
    this.updateEdge(state.edgeId, { source, sourceHandle, target, targetHandle })

    this._emit("reconnect", { edgeId: state.edgeId, type: state.type, newNode: state.target.node, newHandle: state.target.handle })
  }

  // ── Deletion ────────────────────────────────────────────
  // Deletes the selection in one undo-tracked batch: nodes first, then EVERY
  // edge anchored to a removed node (an edge cannot survive a dangling
  // endpoint — cascade beats per-edge deletable), then directly selected
  // edges. Elements whose deletable flag is false are protected instead
  // (same UX as removeHandle's handledeleteblocked).

  _deleteSelected() {
    // Canvas-wide gate (U2): read-only blocks every delete path the engine
    // owns (keyboard Delete/Backspace, Cmd+X's destructive half, context-menu
    // delete). removeNode/removeEdge stay open — remote sync ops and host
    // reducers must keep working on a read-only canvas.
    if (!this.interactive) return
    const deletedNodes = [], blockedNodes = [], blockedEdges = [], ghostEdges = []
    const deletedEdges = new Set()

    this.selectedNodes.forEach(id => {
      const el = this._nodeElement(id)
      if (!el) return
      if (el.dataset["ui-FlowNodeDeletableValue"] === "false") { blockedNodes.push(id); return }
      deletedNodes.push(id)
    })
    this.selectedEdges.forEach(id => {
      const el = this._edgeElement(id)
      // Overlay-only ghost (see _edgeKnown): bare host markup minted the set
      // entry, but the engine holds no PERSISTENT knowledge of this id (no
      // map entry, no edge element). Purge it silently — counting it as a
      // deletion would push a phantom undo entry, broadcast a phantom
      // edgedelete to sync peers, and announce a deletion nothing rendered.
      if (!el && !this._edges.has(id)) { ghostEdges.push(id); return }
      if (el && el.dataset["ui-FlowEdgeDeletableValue"] === "false") { blockedEdges.push(id); return }
      deletedEdges.add(id)
    })
    ghostEdges.forEach(id => this.selectedEdges.delete(id))
    // A purged ghost changes the selected set without emitting (ghosts are
    // event-invisible on delete). Invalidate the dedup cache so re-selecting
    // the same id still emits its selectionchange.
    if (ghostEdges.length > 0) this._lastSelectionSignature = null
    // Cascade: every edge anchored to a removed node goes with it.
    const removedIds = new Set(deletedNodes)
    this._edges.forEach((edge, id) => {
      if (removedIds.has(edge.source) || removedIds.has(edge.target)) deletedEdges.add(id)
    })

    // Blocked-only pass: nothing is being removed, so no undo entry and no
    // clearSelection — but the refusals must still be reported.
    if (deletedNodes.length === 0 && deletedEdges.size === 0) {
      blockedNodes.forEach(id => this._emit("nodedeleteblocked", { nodeId: id }))
      blockedEdges.forEach(id => this._emit("edgedeleteblocked", { edgeId: id }))
      return
    }

    this._pushUndo()
    deletedNodes.forEach(id => {
      const el = this._nodeElement(id)
      this._emit("nodedelete", { nodeId: id })
      el?.remove()
      this._nodeElements.delete(id)
      this._nodes.delete(id)
    })
    deletedEdges.forEach(id => {
      this._emit("edgedelete", { edgeId: id })
      this._edgeElement(id)?.remove()
      this._edgeElements.delete(id)
      this._edges.delete(id)
    })
    blockedNodes.forEach(id => this._emit("nodedeleteblocked", { nodeId: id }))
    blockedEdges.forEach(id => this._emit("edgedeleteblocked", { edgeId: id }))

    const count = deletedNodes.length + deletedEdges.size
    if (count) this._announce("deleted", { count, countPlural: count === 1 ? "" : "s" })
    this.clearSelection()
    if (deletedNodes.length) this._syncEmptyState()
  }

  // edge_controller dispatches edgerequestdelete for a focused, class-selected
  // edge's Delete key. Edges already in selectedEdges are owned by the
  // document-keydown _deleteSelected path (skipped here so the same keypress
  // never double-processes); this consumer covers the class-selected but
  // engine-untracked case. The deletable gate matches every other delete
  // path; removal is undo-tracked through removeEdge.
  _handleEdgeRequestDelete(event) {
    // Canvas-wide gate (U2): read-only blocks the edge request delete (the
    // focused-edge Delete path edge_controller dispatches).
    if (!this.interactive) return
    const edgeId = event.detail?.edgeId
    if (!edgeId) return
    const el = this._edgeElement(edgeId)
    if (!el || !this.element.contains(el)) return // another canvas's edge (S5)
    if (this.selectedEdges.has(edgeId)) return
    if (el.dataset["ui-FlowEdgeDeletableValue"] === "false") { this._emit("edgedeleteblocked", { edgeId }); return }
    this.removeEdge(edgeId)
  }

  // ── Data API ────────────────────────────────────────────

  _loadInitialElements() {
    // Scan nodes: prefer Stimulus value attributes (static HTML), fall back to data-ui--flow-node (set by node controller or tests)
    const nodeEls = this.element.querySelectorAll("[data-ui--flow-node-id-value]")
    if (nodeEls.length > 0) {
      nodeEls.forEach(el => {
        const id = el.dataset["ui-FlowNodeIdValue"]
        if (!id) return
        this._nodes.set(id, {
          id,
          type: el.dataset["ui-FlowNodeTypeValue"] || "default",
          position: { x: parseFloat(el.dataset["ui-FlowNodeXValue"]) || parseFloat(el.style.left) || 0, y: parseFloat(el.dataset["ui-FlowNodeYValue"]) || parseFloat(el.style.top) || 0 },
          ...this._scanOptionalNodeAttrs(el),
          handles: this._readNodeHandles(el)
        })
        this._registerNodeElement(id, el)
      })
    } else if (this.hasNodesTarget) {
      this.nodesTarget.querySelectorAll("[data-ui--flow-node]").forEach(el => {
        const id = el.dataset["ui-FlowNode"]
        this._nodes.set(id, {
          id,
          type: el.dataset.nodeType || "default",
          position: { x: parseFloat(el.style.left) || 0, y: parseFloat(el.style.top) || 0 },
          ...this._scanOptionalNodeAttrs(el),
          handles: this._readNodeHandles(el)
        })
        this._registerNodeElement(id, el)
      })
    }
    // Scan edges: prefer Stimulus value attributes, fall back to data-edge.
    // Lossless at boot (FIX L): _scanEdgeAttrs hydrates label/animated/
    // markers/hidden/deletable/selectable/reconnectable/interactionWidth/
    // curvature/borderRadius/style/labelStyle* from the same data-*
    // attributes edge_controller reads, so getEdge/toObject/undo-restore of
    // an SSR edge keep its full shape instead of the identity-only map.
    const edgeEls = this.element.querySelectorAll("[data-ui--flow-edge-id-value]")
    if (edgeEls.length > 0) {
      edgeEls.forEach(el => {
        const id = el.dataset["ui-FlowEdgeIdValue"]
        if (!id) return
        this._edges.set(id, {
          id,
          source: el.dataset["ui-FlowEdgeSourceValue"] || "",
          target: el.dataset["ui-FlowEdgeTargetValue"] || "",
          sourceHandle: el.dataset["ui-FlowEdgeSourceHandleValue"] || "",
          targetHandle: el.dataset["ui-FlowEdgeTargetHandleValue"] || "",
          type: el.dataset["ui-FlowEdgeTypeValue"] || "bezier",
          ...this._scanEdgeAttrs(el)
        })
        this._registerEdgeElement(id, el)
      })
    }
  }

  // Shared boot-scan parsers (FIX L + wave-8 B1). Booleans parse only the
  // literal "true"/"false" strings; numbers parse the numeric attribute
  // string; strings pass through. Absent/empty attributes stay undefined so
  // absent keys stay OUT of the def (toObject/snapshots don't grow dead
  // keys). `_scanNumNonZero` is the node-only numeric rule: NodeComponent
  // ALWAYS emits width/height/z-index ("0" when unset — number_or_zero), so
  // a literal 0 means "unset" and must not land in the def (do NOT copy the
  // edge scan's ""/undefined-only rule for these — it would resurrect every
  // SSR node with width: 0).
  _scanBool(el, key) { const v = el.dataset[key]; return v === "true" ? true : v === "false" ? false : undefined }
  _scanNum(el, key) { const v = el.dataset[key]; return v === undefined || v === "" ? undefined : Number(v) }
  _scanNumNonZero(el, key) { const n = this._scanNum(el, key); return n ? n : undefined }
  _scanStr(el, key) { const v = el.dataset[key]; return v === undefined || v === "" ? undefined : v }

  // Optional edge-def attrs (FIX L): EdgeComponent emits every non-default
  // value as a data-ui--flow-edge-<kebab>-value attribute, so the ingest can
  // rebuild the full def — getEdge/toObject/_snapshot are lossless at boot,
  // and undo-restore of an SSR edge recovers its real shape. Booleans parse
  // only the literal "true"/"false" strings; numbers parse the numeric
  // attribute string — absent attrs stay undefined (toObject/snapshots don't
  // grow dead keys).
  // The value object's `data` hash has NO SSR attribute (nothing to read) —
  // label still reaches data.label via the edge path here, like nodes.
  _scanEdgeAttrs(el) {
    const bool = key => this._scanBool(el, key)
    const num = key => this._scanNum(el, key)
    let labelStyle, labelBgStyle, labelBgPadding, style
    try { labelStyle = el.dataset["ui-FlowEdgeLabelStyleValue"] ? JSON.parse(el.dataset["ui-FlowEdgeLabelStyleValue"]) : undefined } catch { labelStyle = undefined }
    try { labelBgStyle = el.dataset["ui-FlowEdgeLabelBgStyleValue"] ? JSON.parse(el.dataset["ui-FlowEdgeLabelBgStyleValue"]) : undefined } catch { labelBgStyle = undefined }
    try { labelBgPadding = el.dataset["ui-FlowEdgeLabelBgPaddingValue"] ? JSON.parse(el.dataset["ui-FlowEdgeLabelBgPaddingValue"]) : undefined } catch { labelBgPadding = undefined }
    try { style = el.dataset["ui-FlowEdgeStyleValue"] ? JSON.parse(el.dataset["ui-FlowEdgeStyleValue"]) : undefined } catch { style = undefined }
    const label = el.dataset["ui-FlowEdgeLabelValue"] || undefined
    return {
      label,
      ...(label ? { data: { label } } : {}),
      animated: bool("ui-FlowEdgeAnimatedValue"),
      markerStart: el.dataset["ui-FlowEdgeMarkerStartValue"] || undefined,
      markerEnd: el.dataset["ui-FlowEdgeMarkerEndValue"] || undefined,
      markerColor: el.dataset["ui-FlowEdgeMarkerColorValue"] || undefined,
      hidden: bool("ui-FlowEdgeHiddenValue"),
      deletable: bool("ui-FlowEdgeDeletableValue"),
      selectable: bool("ui-FlowEdgeSelectableValue"),
      reconnectable: bool("ui-FlowEdgeReconnectableValue"),
      interactionWidth: num("ui-FlowEdgeInteractionWidthValue"),
      curvature: num("ui-FlowEdgeCurvatureValue"),
      borderRadius: num("ui-FlowEdgeBorderRadiusValue"),
      labelStyle, labelShowBg: bool("ui-FlowEdgeLabelShowBgValue"),
      labelBgStyle, labelBgPadding,
      labelBgBorderRadius: num("ui-FlowEdgeLabelBgBorderRadiusValue"),
      style,
      status: el.dataset["ui-FlowEdgeStatusValue"] || undefined
    }
  }

  // Optional node-def attrs the SSR markup carries: parentId/extent
  // (sub-flow hierarchy — save/restore must round-trip it) and label
  // (React Flow shape: top-level + data.label, so host-side label logic
  // like search_filter resolves), plus the full presentation/behavior
  // contract NodeComponent emits (wave-8 B1 — the edge scan's FIX L lossless
  // standard applied to nodes: toObject()/getNodes()/exportJSON keep
  // SSR-declared width/height/zIndex/flags, so save→clear→load no longer
  // resurrects nodes visible, unsized, draggable and z-index-reset).
  // Ruby emits "" for nil values and "0" for unset numerics — both map to
  // undefined so absent attrs stay absent from the def (toObject/snapshots
  // don't grow dead keys). `collapsed` rides the ui-flow-node--collapsed
  // CLASS (no value attribute exists — node_controller owns the value).
  _scanOptionalNodeAttrs(el) {
    const label = el.dataset["ui-FlowNodeLabelValue"] || undefined
    const parentId = el.dataset["ui-FlowNodeParentIdValue"] || undefined
    let extent
    try { extent = el.dataset["ui-FlowNodeExtentValue"] ? JSON.parse(el.dataset["ui-FlowNodeExtentValue"]) : undefined } catch { extent = undefined }
    // U19 — lossless data round-trip: NodeComponent#node_data JSON-encodes
    // the host's `data:` hash into this attribute when non-empty (nothing
    // to parse on the common node with no extra data). `label` from its own
    // value attribute is authoritative and applied last, same as the
    // runtime updateNode contract (docs/flow/javascript-api.md: "the
    // data/label mirror holds in both directions").
    let hostData
    try { hostData = el.dataset["ui-FlowNodeDataValue"] ? JSON.parse(el.dataset["ui-FlowNodeDataValue"]) : undefined } catch { hostData = undefined }
    return {
      label,
      data: { ...(hostData || {}), ...(label ? { label } : {}) },
      parentId,
      extent,
      width: this._scanNumNonZero(el, "ui-FlowNodeWidthValue"),
      height: this._scanNumNonZero(el, "ui-FlowNodeHeightValue"),
      zIndex: this._scanNumNonZero(el, "ui-FlowNodeZIndexValue"),
      draggable: this._scanBool(el, "ui-FlowNodeDraggableValue"),
      selectable: this._scanBool(el, "ui-FlowNodeSelectableValue"),
      connectable: this._scanBool(el, "ui-FlowNodeConnectableValue"),
      deletable: this._scanBool(el, "ui-FlowNodeDeletableValue"),
      hidden: this._scanBool(el, "ui-FlowNodeHiddenValue"),
      expandParent: this._scanBool(el, "ui-FlowNodeExpandParentValue"),
      selected: this._scanBool(el, "ui-FlowNodeSelectedValue"),
      dragHandle: this._scanStr(el, "ui-FlowNodeDragHandleValue"),
      sourcePosition: this._scanStr(el, "ui-FlowNodeSourcePositionValue"),
      targetPosition: this._scanStr(el, "ui-FlowNodeTargetPositionValue"),
      collapsed: el.classList.contains("ui-flow-node--collapsed") ? true : undefined
    }
  }

  // ── Id factory hook (U7) ────────────────────────────────
  // Every host/user-facing node-creation path (addNode, paste, drop, menu
  // add, duplicate) asks the host FIRST: a cancelable `ui--flow:beforeadd`
  // dispatch whose `detail.proposedId` the host may REWRITE (the
  // server-minted slug replaces the timestamp id — the M3 editor's hard
  // prerequisite: replaceNode cannot rename, so the persisted id must be
  // final at creation) or CANCEL outright with preventDefault().
  // Returns the FINAL id — null when the add is canceled. A rewritten
  // non-string/blank id is treated as a cancel too, never a silent fallback
  // to the proposal. Snapshot restores (undo/redo/fromObject via
  // _applySnapshot) bypass the hook deliberately — they re-add under the
  // def's own stored id and must not re-fire host prompts.
  // detail: { kind: "add" | "paste" | "drop" | "addnode" | "duplicate",
  //           proposedId, def } — the def object is the one that will be
  // stored, so a host MAY mutate it (mutating proposedId is the contract).
  _assignNodeId(nodeDef, kind) {
    const detail = { kind, proposedId: nodeDef.id ?? `node-${Date.now()}-${this._nodeSeq++}`, def: nodeDef }
    const event = new CustomEvent("ui--flow:beforeadd", { detail, bubbles: true, cancelable: true })
    this.element.dispatchEvent(event)
    if (event.defaultPrevented) return null
    const finalId = detail.proposedId
    // A rewritten non-string/blank id is a CANCEL, never a silent fallback —
    // the host answered, its answer just isn't a usable id.
    if (typeof finalId !== "string" || finalId.trim() === "") return null
    nodeDef.id = finalId
    return finalId
  }

  addNode(nodeDef, idFactoryKind = "add") {
    // U7: the id-factory hook runs first (skipped under the snapshot guard —
    // restore paths own their ids). A canceled add is a full no-op: no map
    // entry, no DOM, no nodesadd.
    if (!this._applyingSnapshot && this._assignNodeId(nodeDef, idFactoryKind) === null) return null
    // Runtime defs rarely declare handles (React Flow node objects don't) —
    // fill the per-type defaults so the stored def is the single source of
    // truth the DOM stub, snapshots, undo/redo and copy/paste all read. An
    // explicit handles array (even empty) is honored as-is. Handles fill
    // AFTER the id lands (they embed the node id).
    if (!nodeDef.handles) nodeDef.handles = this._defaultHandlesFor(nodeDef.id, nodeDef.type)
    this._nodes.set(nodeDef.id, nodeDef)
    this._renderNodeDom(nodeDef)
    this._emit("nodesadd", { nodes: [nodeDef] })
    this._syncEmptyState()
  }
  removeNode(id) {
    // Absent-id no-op (minor #3): sync servers retry ops, and a repeated
    // removeNode must not push an undo entry (or emit nodedelete) for a
    // deletion that already happened — history would fill with no-ops.
    if (!this._nodes.has(id) && !this._nodeElement(id)) return
    this._pushUndo(); this._nodes.delete(id); this._nodeElement(id)?.remove(); this._nodeElements.delete(id); this.selectedNodes.delete(id); this._emitSelectionChange(); this._emit("nodedelete", { nodeId: id }); this._syncEmptyState()
  }

  // ── Runtime node DOM (G1) ───────────────────────────────
  // Twin of _renderEdgeDom: a node added at runtime — addNode, paste,
  // undo/redo restore, remote sync — must become visible, not just live in
  // the map. The stub carries the SAME identity attributes NodeComponent
  // renders server-side (hydration parity), so Stimulus auto-connects
  // ui--flow-node and its connect() re-syncs position/dataset from the values
  // and attaches interaction listeners. style.left/top are set directly too,
  // so the node is correctly placed even before the upgrade tick.

  _renderNodeDom(nodeDef) {
    if (!this.hasNodesTarget) return
    // Cache-first duplicate check, DOM-scan fallback for markup the engine
    // never registered (the cache only covers [data-ui--flow-node] entries;
    // pre-existing value-attribute-only markup still needs the probe).
    if (this._nodeElements.get(nodeDef.id)?.isConnected) return
    if (this.nodesTarget.querySelector(`[data-ui--flow-node="${this._escapeSelectorValue(nodeDef.id)}"], [data-ui--flow-node-id-value="${this._escapeSelectorValue(nodeDef.id)}"]`)) return

    const type = nodeDef.type || "default"
    const x = nodeDef.position?.x ?? 0
    const y = nodeDef.position?.y ?? 0
    const label = nodeDef.label ?? nodeDef.data?.label
    const hidden = nodeDef.hidden === true
    const collapsed = nodeDef.collapsed === true

    const el = document.createElement("div")
    el.setAttribute("class", this._nodeChromeClasses(type, hidden, collapsed))
    el.setAttribute("data-controller", "ui--flow-node")
    el.setAttribute("data-action", "pointerdown->ui--flow-node#pointerDown click->ui--flow-node#click dblclick->ui--flow-node#dblclick")
    // Identity + core values (the hydration-parity set
    // flow_node_component_test.rb pins server-side).
    el.setAttribute("data-ui--flow-node", nodeDef.id)
    el.setAttribute("data-node-type", type)
    el.setAttribute("data-ui--flow-node-id-value", nodeDef.id)
    el.setAttribute("data-ui--flow-node-type-value", type)
    el.setAttribute("data-ui--flow-node-x-value", `${x}`)
    el.setAttribute("data-ui--flow-node-y-value", `${y}`)
    // Label value parity with the server contract (node_component.rb writes
    // it ALWAYS — "" when unset, the mount scan maps "" → undefined):
    // runtime-rendered nodes carry the same always-present attribute, so
    // host-side label logic (search_filter) reads JS-added and
    // server-rendered nodes alike.
    el.setAttribute("data-ui--flow-node-label-value", `${label ?? ""}`)
    // Optional values — emitted only when the def carries them; the node
    // controller's own defaults match the server component's for the rest.
    const optionalValues = {
      draggable: "draggable", selectable: "selectable", connectable: "connectable",
      deletable: "deletable", selected: "selected", hidden: "hidden",
      zIndex: "z-index", dragHandle: "drag-handle", sourcePosition: "source-position",
      targetPosition: "target-position", parentId: "parent-id",
      expandParent: "expand-parent", width: "width", height: "height"
    }
    Object.keys(optionalValues).forEach(key => {
      const value = nodeDef[key]
      if (value === undefined || value === null) return
      el.setAttribute(`data-ui--flow-node-${optionalValues[key]}-value`, `${value}`)
    })
    if (nodeDef.extent !== undefined && nodeDef.extent !== null) {
      // Stimulus Array value — the server serializes :parent as '["parent"]'.
      const extent = nodeDef.extent === "parent" ? ["parent"] : nodeDef.extent
      el.setAttribute("data-ui--flow-node-extent-value", JSON.stringify(extent))
    }
    // The canvas always has an id (CanvasComponent#resolve_canvas_id); the
    // outlet attribute lets the upgraded node resolve the engine for
    // drag/click/keyboard. Without an element id the node still renders and
    // engine-side APIs keep working.
    if (this.element.id) el.setAttribute("data-ui--flow-node-ui--flow-outlet", `#${this.element.id}`)
    // a11y (NodeComponent#a11y_attributes): focusable button spoken as its
    // label; hidden nodes carry no tabindex.
    el.setAttribute("role", "button")
    el.setAttribute("aria-label", `${label ?? nodeDef.id}`)
    if (!hidden) el.setAttribute("tabindex", "0")
    el.style.left = `${x}px`
    el.style.top = `${y}px`

    // Engine class/style projection (NODE-REM): the def's class renders AND
    // is tracked (data-flow-engine-classes), so a later updateNode({class})
    // can retract exactly this set — SSR chrome classes (host) stay
    // untracked (mirror of _renderEdgeDom). Object-form style assigns per key.
    if (nodeDef.class) {
      const classes = `${nodeDef.class}`.split(/\s+/).filter(Boolean)
      classes.forEach(c => el.classList.add(c))
      if (classes.length) el.dataset.flowEngineClasses = classes.join(" ")
    }
    if (nodeDef.style && typeof nodeDef.style === "object") Object.assign(el.style, nodeDef.style)

    // Body fallback = the label text, escaped via textContent (the trusted
    // rich-body contract is server-render only; runtime defs are untrusted).
    if (label !== undefined && label !== null && `${label}` !== "") {
      const body = document.createElement("div")
      body.setAttribute("class", "ui-flow-node__body text-xs leading-relaxed text-foreground")
      body.textContent = `${label}`
      el.appendChild(body)
    }
    // U3 channel projection: defs carrying status/annotations/badges render
    // them at creation (mirroring what updateNode writes — the tracked
    // dataset keys land too, so a later patch/retract stays honest).
    if (nodeDef.status !== undefined) this._syncNodeStatus(el, nodeDef.status)
    if (nodeDef.annotations !== undefined) this._syncNodeAnnotations(el, nodeDef.annotations)
    if (nodeDef.badges !== undefined) this._syncNodeBadges(el, nodeDef.badges)
    ;(nodeDef.handles || []).forEach(handle => this._renderHandleStub(el, nodeDef, handle))

    // Parent sub-flows (spec §4.1): children nest inside their parent's DOM
    // subtree — their style.left/top are relative to it.
    const parentEl = nodeDef.parentId ? this.nodesTarget.querySelector(`[data-ui--flow-node="${this._escapeSelectorValue(nodeDef.parentId)}"]`) : null
    ;(parentEl || this.nodesTarget).appendChild(el)
    this._registerNodeElement(nodeDef.id, el)
  }

  // Mirror of NodeComponent#root_classes — keep in sync with the server
  // component (the Tailwind build already ships every class it renders).
  // border-border (audit ruling): the border token grammar, not the form-field
  // token — the live path drifted to border-input, which made SSR chrome and
  // runtime chrome disagree (W3-B 1).
  _nodeChromeClasses(type, hidden, collapsed = false) {
    const classes = [
      "ui-flow-node", `ui-flow-node--${type}`,
      "bg-card", "border", "border-border", "rounded-lg", "shadow-sm", "px-3", "py-2.5"
    ]
    if (type !== "group") classes.push("w-40", "min-w-32", "max-w-full")
    if (type === "input") classes.push("border-success/60")
    else if (type === "output") classes.push("border-primary/60")
    else if (type === "group") classes.push("border-dashed", "border-muted-foreground/40", "bg-primary/5")
    if (hidden) classes.push("hidden")
    // F4: a def carrying collapsed renders folded (paste / fromObject /
    // undo-of-delete fresh renders) — same def→chrome parity as `hidden`.
    if (collapsed) classes.push("ui-flow-node--collapsed")
    return classes.join(" ")
  }

  // Default handle set per node type — the same shapes the server previews
  // build by hand: an input node exposes only a source (right), an output
  // node only a target (left), group containers none, and default/custom
  // types the connectable pair (target left + source right). Ids follow the
  // server convention `<node-id>-in` / `<node-id>-out`.
  _defaultHandlesFor(nodeId, type) {
    switch (type || "default") {
      case "input":  return [{ id: `${nodeId}-out`, type: "source", position: "right" }]
      case "output": return [{ id: `${nodeId}-in`, type: "target", position: "left" }]
      case "group":  return []
      default: return [
        { id: `${nodeId}-in`, type: "target", position: "left" },
        { id: `${nodeId}-out`, type: "source", position: "right" }
      ]
    }
  }

  // Minimal handle markup (identity attributes + dataset hooks — the latter
  // are what handle_controller.connect() writes at hydration, so the handle
  // is interactive even before Stimulus upgrades it).
  _renderHandleStub(nodeEl, nodeDef, handle) {
    const type = handle.type || "source"
    const position = handle.position || (type === "target" ? "left" : "right")
    const label = nodeDef.label ?? nodeDef.data?.label ?? nodeDef.id

    const el = document.createElement("div")
    el.setAttribute("class", `ui-flow-handle ui-flow-handle--${position} ui-flow-handle--${type}`)
    el.setAttribute("data-controller", "ui--flow-handle")
    el.setAttribute("data-action", "pointerdown->ui--flow-handle#pointerDown")
    el.setAttribute("data-ui--flow-handle-id-value", handle.id)
    el.setAttribute("data-ui--flow-handle-node-id-value", nodeDef.id)
    el.setAttribute("data-ui--flow-handle-type-value", type)
    el.setAttribute("data-ui--flow-handle-position-value", position)
    el.setAttribute("data-ui--flow-handle-deletable-value", `${handle.deletable !== false}`)
    el.setAttribute("data-handle", handle.id)
    el.setAttribute("data-handle-node", nodeDef.id)
    el.setAttribute("data-handle-position", position)
    if (handle.label) el.setAttribute("data-handle-label", handle.label)
    if (handle.kind) el.setAttribute("data-handle-kind", handle.kind)
    if (handle.maxConnections) el.setAttribute("data-handle-max-connections", `${handle.maxConnections}`)
    if (handle.index != null) el.setAttribute("data-handle-index", `${handle.index}`)
    if (handle.kind) el.classList.add(`ui-flow-handle--kind-${handle.kind}`)
    if (handle.label) el.classList.add("ui-flow-handle--labelled")
    const offset = this._handleOffsetFor(nodeDef, handle, position)
    if (offset) {
      el.setAttribute("data-handle-offset", `${offset}`)
      el.style[position === "top" || position === "bottom" ? "left" : "top"] = `${Math.round(offset * 10000) / 100}%`
    }
    if (this.element.id) el.setAttribute("data-ui--flow-handle-ui--flow-outlet", `#${this.element.id}`)
    el.setAttribute("role", "button")
    if (nodeDef.hidden !== true) el.setAttribute("tabindex", "0")
    el.setAttribute("aria-label", handle.label ? `Create connection from ${handle.label} (${label})` : `Create connection from ${label}`)
    if (handle.label) {
      const text = document.createElement("span")
      text.className = `ui-flow-handle__label ui-flow-handle__label--${position}`
      text.setAttribute("aria-hidden", "true")
      text.textContent = handle.label
      el.appendChild(text)
    }
    nodeEl.appendChild(el)
  }

  // Even spacing for handles that share a node side: k/(n+1) in `index` order
  // (then declaration order) — the same rule NodeComponent applies at SSR.
  // null for a side with a single handle (it keeps the classic midpoint).
  _handleOffsetFor(nodeDef, handle, position) {
    const side = (nodeDef.handles || []).filter(h => (h.position || (h.type === "target" ? "left" : "right")) === position)
    if (side.length < 2) return null
    const ordered = side.map((h, i) => ({ h, i }))
      .sort((a, b) => ((a.h.index == null ? 1 : 0) - (b.h.index == null ? 1 : 0)) || ((a.h.index ?? 0) - (b.h.index ?? 0)) || a.i - b.i)
      .map(entry => entry.h)
    const slot = ordered.findIndex(h => h.id === handle.id)
    return slot < 0 ? null : Math.round(((slot + 1) / (side.length + 1)) * 10000) / 10000
  }

  // Handle shapes captured from the live DOM into a node def. The map's defs
  // feed snapshots, undo/redo and copy/paste — without the handle list, a
  // restored or pasted node would re-render handle-less. Own handles only:
  // sub-flow children nest inside the parent's DOM subtree, so a handle whose
  // closest node root isn't THIS element belongs to a child.
  _readNodeHandles(nodeEl) {
    const handles = []
    nodeEl.querySelectorAll("[data-ui--flow-handle-id-value], [data-handle]").forEach(h => {
      if (h.closest("[data-ui--flow-node-id-value], [data-ui--flow-node]") !== nodeEl) return
      const id = h.dataset["ui-FlowHandleIdValue"] || h.dataset.handle
      if (!id) return
      const def = {
        id,
        type: h.dataset["ui-FlowHandleTypeValue"] || (h.classList.contains("ui-flow-handle--target") ? "target" : "source"),
        position: h.dataset["ui-FlowHandlePositionValue"] || h.dataset.handlePosition || "right"
      }
      // Multi-port fields survive snapshot / undo / paste like the rest.
      if (h.dataset.handleLabel) def.label = h.dataset.handleLabel
      if (h.dataset.handleKind) def.kind = h.dataset.handleKind
      const max = parseInt(h.dataset.handleMaxConnections, 10)
      if (Number.isFinite(max)) def.maxConnections = max
      const index = parseInt(h.dataset.handleIndex, 10)
      if (Number.isFinite(index)) def.index = index
      handles.push(def)
    })
    return handles
  }

  // Handle ids must stay unique across the canvas (`[data-handle="..."]` is a
  // document-level lookup) — a copied node can't keep the original's ids.
  // Handles following the `<node-id>-*` convention get the new node's prefix;
  // anything else is namespaced under it.
  _remapHandles(handles, oldNodeId, newNodeId) {
    if (!Array.isArray(handles)) return handles
    return handles.map(h => ({
      ...h,
      id: h.id.startsWith(`${oldNodeId}-`) ? `${newNodeId}-${h.id.slice(oldNodeId.length + 1)}` : `${newNodeId}-${h.id}`
    }))
  }
  addEdge(edgeDef) {
    // Idempotent on id (sync double-add guard): the sync adapter rebroadcasts
    // a local drag-connect as an addEdge op, and a server that echoes ops to
    // the originator (or a peer that already created the same edge locally)
    // would otherwise store a SECOND entry under the same id. Same id →
    // REPLACE (W3-B 3): converge through replaceEdge — the same full-def
    // applier the sync channel's updateEdge op and the restore edge branch
    // use — so after the call defs[id] equals the new def EXACTLY (stale
    // keys leave the map and their DOM projections retract); a key-by-key
    // updateEdge merge would keep keys the new def dropped. No edgesadd (an
    // update is not an add — skipping the emission keeps a broadcast
    // round-trip from re-firing the channel's edgesadd→addEdge forwarding).
    // Non-string ids keep the historical updateEdge merge: replaceEdge's
    // wire-format guard accepts string ids only, and legacy numeric-id maps
    // were never part of the protocol.
    if (edgeDef && edgeDef.id != null && this._edges.has(edgeDef.id)) {
      if (typeof edgeDef.id === "string" && edgeDef.id !== "") this.replaceEdge(edgeDef)
      else this.updateEdge(edgeDef.id, edgeDef)
      return
    }
    this._edges.set(edgeDef.id, edgeDef)
    this._renderEdgeDom(edgeDef)
    this._emit("edgesadd", { edges: [edgeDef] })
  }

  // Dynamic edge rendering (React Flow parity): an edge added at runtime —
  // drag-to-connect, paste, restore, remote sync — must become visible, not
  // just live in the map. The stub projects the FULL def into the value
  // attributes (S1) — identity + type + animated/label/markers/hidden, plus
  // class/style — so a runtime addEdge({animated, label, markerEnd}) renders
  // exactly what the SSR EdgeComponent would. The ui--flow-edge controller
  // auto-connects (Stimulus watches the DOM) and its connect() builds the
  // interaction path, markers, label and computes `d` from these values.
  _renderEdgeDom(edgeDef) {
    if (!this.hasEdgesTarget) return
    if (this._edgeElements.get(edgeDef.id)?.isConnected) return
    if (this.edgesTarget.querySelector(`[data-ui--flow-edge-id-value="${this._escapeSelectorValue(edgeDef.id)}"]`)) return
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("class", "ui-flow-edge")
    path.setAttribute("data-controller", "ui--flow-edge")
    path.setAttribute("data-edge", edgeDef.id)
    path.setAttribute("data-ui--flow-edge-id-value", edgeDef.id)
    path.setAttribute("data-ui--flow-edge-source-value", edgeDef.source)
    if (edgeDef.sourceHandle) path.setAttribute("data-ui--flow-edge-source-handle-value", edgeDef.sourceHandle)
    path.setAttribute("data-ui--flow-edge-target-value", edgeDef.target)
    if (edgeDef.targetHandle) path.setAttribute("data-ui--flow-edge-target-handle-value", edgeDef.targetHandle)
    path.setAttribute("data-ui--flow-edge-type-value", edgeDef.type || "bezier")
    if (edgeDef.animated !== undefined && edgeDef.animated !== null) path.setAttribute("data-ui--flow-edge-animated-value", `${!!edgeDef.animated}`)
    if (edgeDef.label !== undefined && edgeDef.label !== null) path.setAttribute("data-ui--flow-edge-label-value", `${edgeDef.label}`)
    if (edgeDef.markerEnd !== undefined && edgeDef.markerEnd !== null) path.setAttribute("data-ui--flow-edge-marker-end-value", `${edgeDef.markerEnd}`)
    if (edgeDef.markerStart !== undefined && edgeDef.markerStart !== null) path.setAttribute("data-ui--flow-edge-marker-start-value", `${edgeDef.markerStart}`)
    // Wave-8 B3: the three remaining declared edge values join the
    // projection — defaultEdgeOptions carrying them (interactionWidth,
    // labelShowBg, markerColor) was inert for drag-drawn edges because
    // _completeConnection spreads the defaults into the def but only these
    // attributes reach the edge controller's static values.
    if (edgeDef.markerColor !== undefined && edgeDef.markerColor !== null) path.setAttribute("data-ui--flow-edge-marker-color-value", `${edgeDef.markerColor}`)
    if (edgeDef.interactionWidth !== undefined && edgeDef.interactionWidth !== null) path.setAttribute("data-ui--flow-edge-interaction-width-value", `${edgeDef.interactionWidth}`)
    if (edgeDef.labelShowBg !== undefined && edgeDef.labelShowBg !== null) path.setAttribute("data-ui--flow-edge-label-show-bg-value", `${!!edgeDef.labelShowBg}`)
    if (edgeDef.hidden !== undefined && edgeDef.hidden !== null) path.setAttribute("data-ui--flow-edge-hidden-value", `${!!edgeDef.hidden}`)
    // Deletable projection (SSR parity): without the attribute both delete
    // gates (keyboard _deleteSelected, edge toolbar) read the absence as
    // deletable:true and destroy a protected edge.
    if (edgeDef.deletable === false) path.setAttribute("data-ui--flow-edge-deletable-value", "false")
    // Engine-added classes are tracked so a later updateEdge({class}) can
    // retract them without ever touching host classes.
    if (edgeDef.class) {
      const classes = `${edgeDef.class}`.split(/\s+/).filter(Boolean)
      classes.forEach(c => path.classList.add(c))
      if (classes.length) path.dataset.flowEngineClasses = classes.join(" ")
    }
    if (edgeDef.style && typeof edgeDef.style === "object") Object.assign(path.style, edgeDef.style)
    // Status chrome (U5): defs carrying a status render it at creation —
    // class + value attribute, both tracked for later retraction.
    if (edgeDef.status !== undefined) this._syncEdgeStatus(path, edgeDef.status)
    path.setAttribute("stroke-width", "1.5")
    // The presentation attribute lets the arrowhead resolve before Stimulus
    // upgrades the path (the controller's _setupMarkers re-sets it on
    // connect). An explicit markerEnd: "" means NO marker — honor it instead
    // of pointing at a def that was never created.
    if (edgeDef.markerEnd !== "") path.setAttribute("marker-end", `url(#ui-flow-marker-end-${edgeDef.id})`)
    this.edgesTarget.appendChild(path)
    this._registerEdgeElement(edgeDef.id, path)
  }
  removeEdge(id) {
    // Absent-id no-op (minor #3): sync servers retry ops, and a repeated
    // removeEdge must not push an undo entry (or emit edgedelete) for a
    // deletion that already happened — history would fill with no-ops.
    if (!this._edgeKnown(id)) return
    this._pushUndo(); this._edges.delete(id); this._edgeElement(id)?.remove(); this._edgeElements.delete(id); this.selectedEdges.delete(id); this._emitSelectionChange(); this._emit("edgedelete", { edgeId: id })
  }

  getNode(id) { return this._nodes.get(id) || null }
  getNodes() { return Array.from(this._nodes.values()) }
  getEdge(id) { return this._edges.get(id) || null }
  getEdges() { return Array.from(this._edges.values()) }

  getIncomers(nodeId) { const r = []; this._edges.forEach(e => { if (e.target === nodeId) { const n = this._nodes.get(e.source); if (n) r.push(n) } }); return r }
  getOutgoers(nodeId) { const r = []; this._edges.forEach(e => { if (e.source === nodeId) { const n = this._nodes.get(e.target); if (n) r.push(n) } }); return r }
  getConnectedEdges(nodeId) { const r = []; this._edges.forEach(e => { if (e.source === nodeId || e.target === nodeId) r.push(e) }); return r }

  getNodesBounds(nodes) {
    if (!nodes?.length) return { x: 0, y: 0, width: 0, height: 0 }
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity
    nodes.forEach(n => { x1 = Math.min(x1, n.position.x); y1 = Math.min(y1, n.position.y); x2 = Math.max(x2, n.position.x + (n.width || 150)); y2 = Math.max(y2, n.position.y + (n.height || 80)) })
    return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 }
  }

  toObject() { return { nodes: this.getNodes(), edges: this.getEdges(), viewport: { ...this.viewport } } }
  // Restores a toObject()/toolbar "Load" payload: nodes/edges go through the
  // same _applySnapshot path undo/redo uses (node DOM renders via
  // _renderNodeDom, edge DOM via _renderEdgeDom; the `restore`, `nodesadd` and
  // `edgesadd` events all come from that shared path). The restore is ONE
  // undo-stack entry: _pushUndo records the pre-restore graph, then the
  // _applyingSnapshot guard inside _applySnapshot suppresses the per-element
  // pushes removeNode/removeEdge would otherwise each make. Selection is
  // cleared wholesale — a restore can re-anchor ids the user had selected.
  // The incoming payload is deep-copied: _applySnapshot back-fills missing
  // handles into every def, which must not mutate the caller's saved object.
  fromObject(obj) {
    // Canvas-wide gate (U2): read-only blocks the whole-graph write (the
    // toolbar Load path). Restore happens only through setInteractive(true).
    if (!this.interactive) return
    if (!obj) return
    this._pushUndo()
    this.clearSelection()
    if (obj.viewport) this.setViewport(obj.viewport)
    // S3: missing keys keep the current graph — `fromObject({viewport})` is a
    // viewport-only restore, NOT a wipe. Only an EXPLICIT empty array clears
    // (?? over ||: [] is truthy, null/undefined fall through to current).
    this._applySnapshot({
      nodes: JSON.parse(JSON.stringify(obj.nodes ?? this.getNodes())),
      edges: JSON.parse(JSON.stringify(obj.edges ?? this.getEdges()))
    })
  }

  // Live-update a node IN PLACE (React Flow setNodes parity): patches the map
  // entry and the rendered element without re-mounting — its controller,
  // selection state, position and handles all survive. Only the recognized
  // attrs below are touched; nodechange carries the id + applied attrs.
  updateNode(id, attrs = {}) {
    const node = this._nodes.get(id)
    if (!node) return
    // Map sync: label is mirrored into data (React Flow shape) while the
    // runtime def also carries the historical top-level label/body slots —
    // _renderNodeDom reads either, so keep both aligned.
    if (attrs.label !== undefined) { node.label = attrs.label; node.data = { ...(node.data || {}), label: attrs.label } }
    if (attrs.body !== undefined) node.body = attrs.body
    ;["style", "class", "width", "height", "hidden", "draggable", "selectable", "collapsed",
      "status", "annotations", "badges"].forEach(key => {
      if (attrs[key] !== undefined) node[key] = attrs[key]
    })
    const el = this._nodeElement(id)
    if (el) this._patchNodeDom(el, node, attrs)
    // Size-affecting patches (label/body growth, style/class) move the node
    // and re-center its handles — connected edge paths must recompute or
    // they keep the OLD anchors and cut across the grown node. width/height
    // additionally reach edges through the nodechange broadcast (the edge
    // controller's own listener); this direct refresh covers every other
    // attr and is idempotent.
    this._refreshConnectedEdges(id)
    this._emit("nodechange", { nodeId: id, attrs })
  }

  // Recompute the path of every edge anchored to a node (bounded by the
  // node's degree). The edge controller caches node dims per id — invalidate
  // the entry first so the recompute reads the CURRENT box.
  _refreshConnectedEdges(nodeId) {
    this.getConnectedEdges(nodeId).forEach(edge => {
      const el = this._edgeElement(edge.id)
      if (!el) return
      let controller = null
      try { controller = this.application?.getControllerForElementAndIdentifier(el, "ui--flow-edge") || null } catch { controller = null }
      controller?._dims?.delete(nodeId)
      controller?.refresh?.()
    })
  }

  // Facade for the search/filter scenario: hide/show one node.
  setNodeHidden(id, hidden) { this.updateNode(id, { hidden }) }

  // Fold one node's body while header/label + handles stay (F4 — distinct
  // from group collapse, which hides whole child nodes). Routes through
  // updateNode so the map def's `collapsed` flag (toObject/snapshots carry
  // it), the element class and the single nodechange emission all land
  // together; the node's declared width/height are untouched — collapse is
  // presentation. Unknown/ghost id: updateNode's absent-def guard no-ops,
  // exactly like setNodeHidden's path.
  toggleNodeCollapsed(id) {
    const node = this._nodes.get(id)
    if (!node) return
    this.updateNode(id, { collapsed: !node.collapsed })
  }

  // Swap ONE node's body markup in place (U10 — the M3 inspector re-renders a
  // node's server-built summary after every config edit). Unlike updateNode's
  // body (escaped TEXT — a JS data-API sink has no per-string trust marker),
  // this takes TRUSTED HTML: the caller vouches for it, exactly like the
  // server component's `node.body` .html_safe contract. Never pass user
  // input through it unescaped.
  //
  // Only the `.ui-flow-node__body` slot's children change — handles, badge
  // strip, selection, position and edges are untouched, and no engine map is
  // rebuilt (the def's `body` text is NOT rewritten: rich markup is view
  // state, so toObject/sync ops never carry it, and a later
  // updateNode({body})/replaceNode that re-projects body text replaces it).
  // Stimulus controllers inside the new markup connect and the old markup's
  // disconnect through Stimulus' own observer. A node without a body slot
  // (no label/body at render) gets one, placed before the badge strip /
  // handles like NodeComponent. Connected edges refresh (the body can change
  // the node's box) and `ui--flow:nodebodychange {nodeId}` fires. No undo
  // push — it is presentation. Returns true when swapped; false (no-op) for
  // an unknown id, a node whose element is gone, or non-string input.
  replaceNodeBody(id, trustedHtml) {
    if (typeof id !== "string" || id === "" || typeof trustedHtml !== "string") {
      console.warn("[ui--flow] replaceNodeBody(id, trustedHtml) takes a node id and an HTML string", id, trustedHtml)
      return false
    }
    if (!this._nodes.has(id)) return false
    const el = this._nodeElement(id)
    if (!el) return false
    // Own slot only: a group node's DOM nests its children's bodies, and a
    // descendant query would swap the wrong one.
    let body = Array.from(el.children).find(child => child.classList.contains("ui-flow-node__body"))
    if (!body) {
      body = document.createElement("div")
      body.setAttribute("class", "ui-flow-node__body text-xs leading-relaxed text-foreground")
      const anchor = Array.from(el.children).find(child => child.classList.contains("ui-flow-node__badges") || child.hasAttribute("data-handle"))
      el.insertBefore(body, anchor || null)
    }
    // The label-only fallback body is line-clamped; rich markup is not.
    body.classList.remove("line-clamp-3")
    body.innerHTML = trustedHtml
    this._refreshConnectedEdges(id)
    this._emit("nodebodychange", { nodeId: id })
    return true
  }

  // Full-def node REPLACE (protocol): converge the map entry to EXACTLY the
  // given def — a key the def drops loses its map entry AND its DOM
  // projection (retracted to its default first by _retractNodeDomKeys, the
  // node-side mirror of _retractEdgeDomKeys — updateNode only re-syncs keys
  // the def CARRIES, so a stayed header/body text, engine class, inline
  // style prop or display:none would keep showing what the map no longer
  // says), then updateNode applies the carried keys, re-projects, refreshes
  // connected edges and emits nodechange. A key-by-key updateNode merge
  // never removes — a peer applying a full-def op would keep stale keys the
  // sender no longer has. This is the apply path the sync channel's
  // updateNode op uses, and what the restore branch calls after its own
  // equality skip (replaceNode itself always converges — no skip here).
  // Unknown id: the same silent no-op as updateNode's absent-def guard (a
  // peer's add-then-replace sequence must not diverge on a def that never
  // arrived). No undo push — callers (restore paths, the sync applier) own
  // their history.
  // STRUCTURAL keys follow a different rule than presentation keys: position
  // is moveNode's op domain, so it NEVER participates — def-absent position
  // KEEPS the existing one, def-carried position is IGNORED (this preserves
  // the documented "position-less restore projects nothing it doesn't carry"
  // promise, pinned in protocol_update_node.test.ts). handles follow the
  // same keep-when-absent rule — the _applySnapshot add path back-fills
  // handles, so a partial host payload must not reset custom handles — while
  // handles the def CARRIES do converge (map-level; handle stubs are not
  // re-rendered in place).
  replaceNode(nodeDef) {
    if (!nodeDef || typeof nodeDef !== "object" || typeof nodeDef.id !== "string" || nodeDef.id === "") {
      console.warn("[ui--flow] replaceNode(nodeDef) takes a single def object with a non-empty string id", nodeDef)
      return
    }
    const existing = this._nodes.get(nodeDef.id)
    if (!existing) return
    // Capture the dropped keys BEFORE the delete, and check OWN keys only:
    // `key in nDef` answers true down the prototype chain, so an own key
    // named `constructor`/`toString` would never read as dropped — the stale
    // key and its projection would survive the replace. position/handles are
    // excluded from the scan: STRUCTURAL keys keep when absent (see above).
    const dropped = Object.keys(existing).filter(key => key !== "position" && key !== "handles" && !Object.prototype.hasOwnProperty.call(nodeDef, key)).map(key => [key, existing[key]])
    if (dropped.length) {
      const el = this._nodeElement(nodeDef.id)
      if (el) this._retractNodeDomKeys(el, dropped, existing)
    }
    Object.keys(existing).forEach(key => {
      if (key === "position" || key === "handles") return
      if (!Object.prototype.hasOwnProperty.call(nodeDef, key)) delete existing[key]
    })
    // Converge scope: every key the def CARRIES except the two structural
    // exclusions above assigns onto the live entry. updateNode alone
    // recognizes only its patch subset (label/body/style/class/width/height/
    // hidden/draggable/selectable/collapsed), so data/handles/extent and the
    // other full-def slots converge by this direct assignment; updateNode
    // re-applies its subset idempotently and owns the projection + emission.
    Object.keys(nodeDef).forEach(key => {
      if (key === "position") return
      existing[key] = nodeDef[key]
    })
    // data-label mirror BACKWARD (updateNode's forward label → data.label
    // mirror only runs when the def carries label; readers accept either
    // slot, so a def carrying data.label without label syncs label FROM
    // data.label — one direction only would leave the pair inconsistent).
    // The label goes back through updateNode via a COPY extension (the map
    // assignment above already placed data wholesale; updateNode's own
    // forward mirror then re-derives data.label idempotently) so the label
    // also re-projects into the DOM slots — a map-only sync would leave the
    // header/aria-label showing the retracted empty default.
    let attrsForUpdate = nodeDef
    if (nodeDef.label === undefined && nodeDef.data && nodeDef.data.label !== undefined) {
      attrsForUpdate = { ...nodeDef, label: nodeDef.data.label }
    }
    this.updateNode(nodeDef.id, attrsForUpdate)
  }

  // Attribute-level edge patch (React Flow setEdges parity). F11: every
  // recognized attr is synced to its data-ui--flow-edge-*-value attribute —
  // the contract _completeReconnect's rewire rides — so the edge
  // controller's refresh() re-derives path/type/markers/label from values
  // that actually match the map (previously only `label` and `class` reached
  // the DOM; type/animated/marker*/identity patches were render-inert).
  updateEdge(id, attrs = {}) {
    // The sync channel's updateEdge op carries the full fresh def — the
    // one-argument shape (the def carries its own id) exists for that
    // full-def caller family; the channel's applier now routes it to
    // replaceEdge for exact convergence. Accept the shape alongside the
    // primary (id, attrs) form. A second argument in this shape is
    // unreachable (the def replaces it) — name the loss instead of silently
    // dropping it.
    if (id && typeof id === "object") {
      if (arguments.length > 1) console.warn("[ui--flow] updateEdge(edgeDef) takes a single argument — the attrs argument was discarded; patch by id with updateEdge(id, attrs)", attrs)
      attrs = id; id = id.id
    }
    const edge = this._edges.get(id)
    if (!edge) return
    Object.assign(edge, attrs)
    const el = this._edgeElement(id)
    if (el) {
      if (attrs.source !== undefined) el.setAttribute("data-ui--flow-edge-source-value", `${attrs.source}`)
      if (attrs.target !== undefined) el.setAttribute("data-ui--flow-edge-target-value", `${attrs.target}`)
      if (attrs.sourceHandle !== undefined) {
        if (attrs.sourceHandle) el.setAttribute("data-ui--flow-edge-source-handle-value", `${attrs.sourceHandle}`)
        else el.removeAttribute("data-ui--flow-edge-source-handle-value")
      }
      if (attrs.targetHandle !== undefined) {
        if (attrs.targetHandle) el.setAttribute("data-ui--flow-edge-target-handle-value", `${attrs.targetHandle}`)
        else el.removeAttribute("data-ui--flow-edge-target-handle-value")
      }
      if (attrs.type !== undefined) el.setAttribute("data-ui--flow-edge-type-value", `${attrs.type}`)
      if (attrs.animated !== undefined) {
        el.setAttribute("data-ui--flow-edge-animated-value", `${!!attrs.animated}`)
        // connect() applies the class once — a live toggle must follow.
        el.classList.toggle("animated", !!attrs.animated)
      }
      if (attrs.markerEnd !== undefined) el.setAttribute("data-ui--flow-edge-marker-end-value", `${attrs.markerEnd}`)
      if (attrs.markerStart !== undefined) el.setAttribute("data-ui--flow-edge-marker-start-value", `${attrs.markerStart}`)
      if (attrs.markerColor !== undefined) el.setAttribute("data-ui--flow-edge-marker-color-value", `${attrs.markerColor}`)
      if (attrs.label !== undefined) el.setAttribute("data-ui--flow-edge-label-value", `${attrs.label}`)
      if (attrs.labelStyle !== undefined) el.setAttribute("data-ui--flow-edge-label-style-value", JSON.stringify(attrs.labelStyle))
      if (attrs.labelShowBg !== undefined) el.setAttribute("data-ui--flow-edge-label-show-bg-value", `${!!attrs.labelShowBg}`)
      if (attrs.labelBgStyle !== undefined) el.setAttribute("data-ui--flow-edge-label-bg-style-value", JSON.stringify(attrs.labelBgStyle))
      if (attrs.labelBgPadding !== undefined) el.setAttribute("data-ui--flow-edge-label-bg-padding-value", JSON.stringify(attrs.labelBgPadding))
      if (attrs.labelBgBorderRadius !== undefined) el.setAttribute("data-ui--flow-edge-label-bg-border-radius-value", `${attrs.labelBgBorderRadius}`)
      if (attrs.hidden !== undefined) {
        el.setAttribute("data-ui--flow-edge-hidden-value", `${!!attrs.hidden}`)
        // connect() parity: display none + no tabindex while hidden. Un-hide
        // restores tabindex ONLY where it existed before the hide — an edge
        // SSR'd without one (non-selectable) must not gain focusability.
        el.style.display = attrs.hidden ? "none" : ""
        if (attrs.hidden) {
          if (el.hasAttribute("tabindex")) el.dataset.flowHadTabindex = "true"
          el.removeAttribute("tabindex")
        } else if (el.dataset.flowHadTabindex) {
          el.setAttribute("tabindex", "0")
          delete el.dataset.flowHadTabindex
        }
      }
      if (attrs.deletable !== undefined) el.setAttribute("data-ui--flow-edge-deletable-value", `${!!attrs.deletable}`)
      if (attrs.selectable !== undefined) el.setAttribute("data-ui--flow-edge-selectable-value", `${!!attrs.selectable}`)
      if (attrs.curvature !== undefined) el.setAttribute("data-ui--flow-edge-curvature-value", `${attrs.curvature}`)
      if (attrs.borderRadius !== undefined) el.setAttribute("data-ui--flow-edge-border-radius-value", `${attrs.borderRadius}`)
      if (attrs.offset !== undefined) el.setAttribute("data-ui--flow-edge-offset-value", `${attrs.offset}`)
      if (attrs.interactionWidth !== undefined) el.setAttribute("data-ui--flow-edge-interaction-width-value", `${attrs.interactionWidth}`)
      if (attrs.class !== undefined) {
        // Replace the engine-managed set (tracked in data-flow-engine-classes
        // by _renderEdgeDom and here) — a retracted preset actually leaves,
        // host classes are never touched.
        ;(el.dataset.flowEngineClasses || "").split(/\s+/).filter(Boolean).forEach(c => el.classList.remove(c))
        const next = `${attrs.class}`.split(/\s+/).filter(Boolean)
        next.forEach(c => el.classList.add(c))
        if (next.length) el.dataset.flowEngineClasses = next.join(" ")
        else delete el.dataset.flowEngineClasses
      }
      // Status chrome (U5) routes through its own tracked slot — a class
      // write must never wipe it, and null/"" retracts it.
      if (attrs.status !== undefined) this._syncEdgeStatus(el, attrs.status)
      if (attrs.style !== undefined) {
        if (typeof attrs.style === "string") {
          // Replace, don't accumulate: remove the properties the engine's
          // PREVIOUS string-style write set (tracked by name), then apply the
          // new string. A plain cssText += grows the attribute with dead
          // declarations on every update. (A property the HOST set inline is
          // removed too if the engine previously claimed it — document the
          // string form as engine-owned.)
          ;(el.dataset.flowEngineStyleProps || "").split(" ").filter(Boolean).forEach(p => el.style.removeProperty(p))
          const probe = document.createElement("div")
          probe.style.cssText = attrs.style
          const props = []
          for (let i = 0; i < probe.style.length; i++) props.push(probe.style[i])
          props.forEach(p => el.style.setProperty(p, probe.style.getPropertyValue(p)))
          if (props.length) el.dataset.flowEngineStyleProps = props.join(" ")
          else delete el.dataset.flowEngineStyleProps
        } else {
          Object.assign(el.style, attrs.style)
        }
      }
      let controller = null
      try { controller = this.application?.getControllerForElementAndIdentifier(el, "ui--flow-edge") || null } catch { controller = null }
      if (controller) {
        // Label re-render belongs to edge_controller's labelValueChanged
        // callbacks (EDGE-FIX _rebuildLabel) — the flow-side poke that used
        // to drop a stale group here is gone with it. Markers are still
        // created once at connect from the marker values — drop and
        // re-create so updateEdge({markerEnd}) shows.
        if ((attrs.markerEnd !== undefined || attrs.markerStart !== undefined) && typeof controller._setupMarkers === "function") {
          controller._markers?.forEach(m => m.remove())
          controller._markers = []
          el.removeAttribute("marker-end")
          el.removeAttribute("marker-start")
          controller._setupMarkers()
        }
        controller.refresh?.()
      }
    }
    this._emit("edgechange", { edgeId: id, attrs })
  }

  // Full-def edge REPLACE (protocol): converge the map entry to EXACTLY the
  // given def — a key the def drops loses its map entry AND its DOM
  // projection (retracted to its default first, the way _applySnapshot's B1
  // branch does it — updateEdge only re-syncs keys the def CARRIES, so a
  // stayed data attribute / inline style / display:none would keep showing
  // what the map no longer says; the outgoing values ride along because
  // class/style retraction needs them), then updateEdge applies the carried
  // keys, re-renders from clean defaults and emits edgechange. A key-by-key
  // updateEdge merge never removes — a peer applying a full-def op would
  // keep stale keys the sender no longer has. This is the apply path the
  // sync channel's updateEdge op uses, and what B1 calls after its own
  // equality skip (replaceEdge itself always converges — no skip here).
  // Unknown id: the same silent no-op as updateEdge (a peer's add-then-
  // replace sequence must not diverge on a def that never arrived). No undo
  // push — callers (restore paths, the sync applier) own their history.
  replaceEdge(edgeDef) {
    if (!edgeDef || typeof edgeDef !== "object" || typeof edgeDef.id !== "string" || edgeDef.id === "") {
      console.warn("[ui--flow] replaceEdge(edgeDef) takes a single def object with a non-empty string id", edgeDef)
      return
    }
    const existing = this._edges.get(edgeDef.id)
    if (!existing) return
    // Capture the dropped keys BEFORE the delete, and check OWN keys only:
    // `key in eDef` answers true down the prototype chain, so an own key
    // named `constructor`/`toString` would never read as dropped — the stale
    // key and its projection would survive the replace.
    const dropped = Object.keys(existing).filter(key => !Object.prototype.hasOwnProperty.call(edgeDef, key)).map(key => [key, existing[key]])
    if (dropped.length) {
      const el = this._edgeElement(edgeDef.id)
      if (el) this._retractEdgeDomKeys(el, dropped)
    }
    Object.keys(existing).forEach(key => { if (!Object.prototype.hasOwnProperty.call(edgeDef, key)) delete existing[key] })
    this.updateEdge(edgeDef.id, edgeDef)
  }

  // Snapshot-restore retraction for the keys an edge def DROPS (backlog —
  // see _applySnapshot's edge branch): write each key's DEFAULT DOM
  // projection, mirroring the attribute names updateEdge writes. The value
  // attributes simply come off — the edge controller declares its Stimulus
  // defaults for the ABSENT attribute (type "bezier", markerEnd
  // "arrowclosed", labelShowBg true, curvature 0.25, …), so removal IS the
  // default-restoring write — while the projections that live outside a
  // value attribute (the animated class, hidden's display + parked
  // tabindex, engine classes, inline style, the rendered markers) are torn
  // down exactly the way updateEdge's replace paths do it. Identity keys
  // (id/source/target) have no default and projection-less custom keys
  // (data, …) have nothing to retract — both are skipped.
  _retractEdgeDomKeys(el, dropped) {
    const SIMPLE = {
      sourceHandle: "data-ui--flow-edge-source-handle-value",
      targetHandle: "data-ui--flow-edge-target-handle-value",
      type: "data-ui--flow-edge-type-value",
      label: "data-ui--flow-edge-label-value",
      labelStyle: "data-ui--flow-edge-label-style-value",
      labelShowBg: "data-ui--flow-edge-label-show-bg-value",
      labelBgStyle: "data-ui--flow-edge-label-bg-style-value",
      labelBgPadding: "data-ui--flow-edge-label-bg-padding-value",
      labelBgBorderRadius: "data-ui--flow-edge-label-bg-border-radius-value",
      markerColor: "data-ui--flow-edge-marker-color-value",
      deletable: "data-ui--flow-edge-deletable-value",
      selectable: "data-ui--flow-edge-selectable-value",
      curvature: "data-ui--flow-edge-curvature-value",
      borderRadius: "data-ui--flow-edge-border-radius-value",
      offset: "data-ui--flow-edge-offset-value",
      interactionWidth: "data-ui--flow-edge-interaction-width-value",
      reconnectable: "data-ui--flow-edge-reconnectable-value"
    }
    let markersDropped = false
    dropped.forEach(([key, oldValue]) => {
      if (SIMPLE[key]) { el.removeAttribute(SIMPLE[key]); return }
      switch (key) {
        case "animated":
          el.removeAttribute("data-ui--flow-edge-animated-value")
          el.classList.remove("animated") // updateEdge's live toggle, reversed
          break
        case "markerEnd":
          el.removeAttribute("data-ui--flow-edge-marker-end-value")
          markersDropped = true
          break
        case "markerStart":
          el.removeAttribute("data-ui--flow-edge-marker-start-value")
          markersDropped = true
          break
        case "hidden":
          el.removeAttribute("data-ui--flow-edge-hidden-value")
          // updateEdge's un-hide, replayed: display resets and tabindex
          // comes back ONLY where a hide had parked it.
          el.style.display = ""
          if (el.dataset.flowHadTabindex) { el.setAttribute("tabindex", "0"); delete el.dataset.flowHadTabindex }
          break
        case "class":
          // updateEdge's class write with an empty set: retract exactly the
          // engine-tracked classes — host classes are never touched.
          ;(el.dataset.flowEngineClasses || "").split(/\s+/).filter(Boolean).forEach(c => el.classList.remove(c))
          delete el.dataset.flowEngineClasses
          break
        case "status":
          // U5 retraction: the tracked status class and the value attribute
          // leave together (the attribute removal is also the scan default —
          // an absent attribute maps to undefined, a dead key).
          if (el.dataset.flowEngineStatusClass) {
            el.classList.remove(el.dataset.flowEngineStatusClass)
            delete el.dataset.flowEngineStatusClass
          }
          el.removeAttribute("data-ui--flow-edge-status-value")
          break
        case "style":
          // updateEdge's string-form replace with an empty string: remove
          // the engine-tracked properties, falling back to parsing the
          // outgoing string when the engine didn't write it (SSR markup).
          // Object form was never tracked — retract the def's own keys.
          ;(el.dataset.flowEngineStyleProps || "").split(" ").filter(Boolean).forEach(p => el.style.removeProperty(p))
          if (typeof oldValue === "string") {
            const probe = document.createElement("div")
            probe.style.cssText = oldValue
            for (let i = 0; i < probe.style.length; i++) el.style.removeProperty(probe.style[i])
          } else if (oldValue && typeof oldValue === "object") {
            Object.keys(oldValue).forEach(p => el.style.removeProperty(p.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)))
          }
          delete el.dataset.flowEngineStyleProps
          break
      }
    })
    if (markersDropped) {
      // Mirror updateEdge's marker swap: drop the rendered markers and let
      // the controller re-create them from the restored defaults (an absent
      // markerEnd revives the "arrowclosed" default).
      let controller = null
      try { controller = this.application?.getControllerForElementAndIdentifier(el, "ui--flow-edge") || null } catch { controller = null }
      if (controller && typeof controller._setupMarkers === "function") {
        controller._markers?.forEach(m => m.remove())
        controller._markers = []
        el.removeAttribute("marker-end")
        el.removeAttribute("marker-start")
        controller._setupMarkers()
      }
    }
  }

  // Edge status chrome (U5): one engine-owned class on the path element
  // (ui-flow-edge--status-<name>), tracked in a DEDICATED dataset key — NOT
  // data-flow-engine-classes, which the class channel replaces wholesale (a
  // status write must survive updateEdge({class}) and vice versa). The
  // data-ui--flow-edge-status-value attribute mirrors the state for the boot
  // scan (_scanEdgeAttrs) so an SSR/runtime edge round-trips its status
  // through toObject/undo; both projections leave together on a null/empty
  // write. Whitespace collapses to "-" so an unexpected value can never make
  // classList.add throw.
  _syncEdgeStatus(el, status) {
    const previous = el.dataset.flowEngineStatusClass
    if (previous) { el.classList.remove(previous); delete el.dataset.flowEngineStatusClass }
    if (status === undefined || status === null || `${status}`.trim() === "") {
      el.removeAttribute("data-ui--flow-edge-status-value")
      return
    }
    const name = `${status}`.trim().replace(/\s+/g, "-")
    const className = `ui-flow-edge--status-${name}`
    el.classList.add(className)
    el.dataset.flowEngineStatusClass = className
    el.setAttribute("data-ui--flow-edge-status-value", name)
  }

  // Deep equality for edge defs — B1's no-op-restore check. Same own-key
  // set, order-independent (a JSON snapshot def and a live map def order
  // keys differently, so JSON.stringify would false-negative): primitives
  // by Object.is (NaN included), plain objects/arrays recursively, functions
  // by identity. Keys with undefined VALUES count as absent on both sides —
  // the boot scan carries them (animated: undefined, …) while JSON
  // snapshots drop them, so a valueless key must not read as a difference
  // or no SSR edge would ever compare equal. Object.is's pickiness
  // (-0 ≠ +0, NaN self-equal) is the CONSERVATIVE direction: a cosmetic
  // numeric difference reads as unequal, so the no-op skip is not taken and
  // the full retraction path runs — at worst redundant work, never a wrong
  // skip.
  _edgeDefsEqual(a, b) {
    if (Object.is(a, b)) return true
    if (!a || !b || typeof a !== "object" || typeof b !== "object") return false
    const aKeys = Object.keys(a).filter(k => a[k] !== undefined)
    const bKeys = Object.keys(b).filter(k => b[k] !== undefined)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every(key => Object.prototype.hasOwnProperty.call(b, key) && this._edgeDefsEqual(a[key], b[key]))
  }

  // Snapshot-restore retraction for the keys a node def DROPS (node-side
  // mirror of _retractEdgeDomKeys — see replaceNode): write each key's
  // DEFAULT DOM projection, mirroring the projection updateNode/_patchNodeDom
  // writes. The SIMPLE value attributes come off so the node controller's own
  // Stimulus defaults apply (draggable/selectable true, …), while the
  // projections that live outside a value attribute (header/body text +
  // aria-label, hidden's display:none, engine classes, inline style,
  // width/height inline positioning, the collapsed class) are torn down
  // exactly the way updateNode's write paths do it. Identity keys (id/type)
  // and payload keys (data) have nothing to retract. Keys whose render
  // projection is an add-time value attribute (extent →
  // data-ui--flow-node-extent-value, read by _moveNode's parent clamp;
  // parentId, …) are structural by ruling: the map entry converges, but the
  // add-time attribute is NOT re-processed — a def that drops one leaves a
  // stale value attribute behind (the sync protocol always carries the full
  // read-back def, so this window is host-direct replaceNode calls only).
  // STRUCTURAL exclusions (position, handles) never reach this list — the
  // keep-when-absent rule above, so a handle stub set never gets torn down
  // by a partial def.
  _retractNodeDomKeys(el, dropped, oldDef) {
    const SIMPLE = {
      draggable: "data-ui--flow-node-draggable-value",
      selectable: "data-ui--flow-node-selectable-value",
      width: "data-ui--flow-node-width-value",
      height: "data-ui--flow-node-height-value"
    }
    dropped.forEach(([key, oldValue]) => {
      if (SIMPLE[key]) {
        el.removeAttribute(SIMPLE[key])
        // width/height additionally project inline (updateNode writes
        // style.width/style.height when truthy) — reset the inline write
        // alongside the attribute removal.
        if (key === "width") el.style.width = ""
        if (key === "height") el.style.height = ""
        return
      }
      switch (key) {
        case "label":
        case "data": {
          // A dropped `data` payload only matters for the label projection
          // when the rendered label CAME from data.label: retract iff the old
          // def carries no own `label` (own-key check — prototype-chain keys
          // must not count) AND the dropped data object has `data.label`
          // non-null. An own label survives a data drop; any other data drop
          // is visually inert. Otherwise (label drop, or a qualifying data
          // drop) perform exactly the label retraction. The own-label guard is
          // defensive: on every public path replaceNode's converge re-projects
          // a carried label immediately after this retraction, so no
          // public-API pin can isolate the guard — it exists for direct
          // map-manipulation correctness.
          if (key === "data") {
            if (Object.prototype.hasOwnProperty.call(oldDef || {}, "label")) break
            if (oldValue == null || oldValue.label == null) break
          }
          // Default label slot is EMPTY; aria falls back to the node id (the
          // _renderNodeDom/_patchNodeDom fallback chain — label ?? id).
          const header = el.querySelector(".ui-flow-node__header")
          if (header) header.textContent = ""
          el.setAttribute("aria-label", `${oldDef?.id ?? ""}`)
          // The body slot shows the label as a fallback when the old def had
          // no explicit body (own-key check — prototype-chain keys must not
          // count as present, same ruling as replaceNode's dropped scan) —
          // clear it too; an explicit body stays and re-projects below.
          if (!Object.prototype.hasOwnProperty.call(oldDef || {}, "body")) {
            const body = el.querySelector(".ui-flow-node__body")
            if (body) body.textContent = ""
          }
          // The always-present label value attribute retracts to "" (NEVER
          // removeAttribute — the server contract writes it always; see
          // _renderNodeDom).
          el.setAttribute("data-ui--flow-node-label-value", "")
          break
        }
        case "body": {
          const body = el.querySelector(".ui-flow-node__body")
          if (body) body.textContent = ""
          break
        }
        case "hidden":
          el.removeAttribute("data-ui--flow-node-hidden-value")
          // updateNode's un-hide, replayed: display resets to visible. The
          // node controller's hide()/show() contract only ever touches the
          // hidden value + display (no tabindex parking on the node side),
          // so attribute removal + display reset IS the default write.
          el.style.display = ""
          break
        case "class":
          // updateNode's class write with an empty set: retract exactly the
          // engine-tracked classes (data-flow-engine-classes) — host classes
          // are never touched.
          ;(el.dataset.flowEngineClasses || "").split(/\s+/).filter(Boolean).forEach(c => el.classList.remove(c))
          delete el.dataset.flowEngineClasses
          break
        case "style":
          // updateNode's string-form replace with an empty string: remove
          // the engine-tracked properties, falling back to parsing the
          // outgoing string when the engine didn't write it (SSR markup).
          // Object form was never tracked — retract the def's own keys
          // (mirror of the edge side's camelCase → kebab-case remove).
          ;(el.dataset.flowEngineStyleProps || "").split(" ").filter(Boolean).forEach(p => el.style.removeProperty(p))
          if (typeof oldValue === "string") {
            const probe = document.createElement("div")
            probe.style.cssText = oldValue
            for (let i = 0; i < probe.style.length; i++) el.style.removeProperty(probe.style[i])
          } else if (oldValue && typeof oldValue === "object") {
            Object.keys(oldValue).forEach(p => el.style.removeProperty(p.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)))
          }
          delete el.dataset.flowEngineStyleProps
          break
        case "collapsed":
          el.classList.remove("ui-flow-node--collapsed")
          break
        case "status":
          // U3 retraction: drop exactly the tracked status class (the
          // projection lives outside any value attribute).
          if (el.dataset.flowEngineStatusClass) {
            el.classList.remove(el.dataset.flowEngineStatusClass)
            delete el.dataset.flowEngineStatusClass
          }
          break
        case "annotations":
          // U3 retraction: drop the tracked annotation set wholesale.
          ;(el.dataset.flowEngineAnnotations || "").split(/\s+/).filter(Boolean).forEach(name => el.classList.remove(`ui-flow-node--annotation-${name}`))
          delete el.dataset.flowEngineAnnotations
          break
        case "badges":
          // U3 retraction: only the ENGINE-OWNED strip comes off — an
          // authored NodeComponent badge slot is host chrome, never touched.
          el.querySelector(".ui-flow-node__badges[data-flow-engine-badges]")?.remove()
          break
      }
    })
  }

  // Deep equality for node defs — the restore branch's no-op-restore check.
  // Exact mirror of _edgeDefsEqual (own-key set, order-independent,
  // undefined values count as absent on both sides, Object.is's -0/NaN
  // pickiness is the CONSERVATIVE direction), with position stripped from
  // both sides before comparison: the restore branch handles position first
  // through _moveNode (the ONE moveNode op), so including it would break the
  // skip for position-only restores — undo of a pure move must broadcast
  // nothing beyond its one moveNode.
  _nodeDefsEqual(a, b) {
    const strip = def => def && typeof def === "object" ? Object.keys(def).reduce((out, k) => { if (k !== "position") out[k] = def[k]; return out }, {}) : def
    const aNoPos = strip(a)
    const bNoPos = strip(b)
    if (Object.is(aNoPos, bNoPos)) return true
    if (!aNoPos || !bNoPos || typeof aNoPos !== "object" || typeof bNoPos !== "object") return false
    const aKeys = Object.keys(aNoPos).filter(k => aNoPos[k] !== undefined)
    const bKeys = Object.keys(bNoPos).filter(k => bNoPos[k] !== undefined)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every(key => Object.prototype.hasOwnProperty.call(bNoPos, key) && this._nodeDefsEqual(aNoPos[key], bNoPos[key]))
  }

  // DOM half of updateNode: mutates the existing element in place. Label/body
  // target the server component's known slots (.ui-flow-node__header eyebrow,
  // .ui-flow-node__body), falling back to a label-only body.
  _patchNodeDom(el, node, attrs) {
    if (attrs.label !== undefined || attrs.body !== undefined) {
      const label = node.label ?? node.data?.label
      const header = el.querySelector(".ui-flow-node__header")
      if (header) header.textContent = `${label ?? ""}`
      const body = el.querySelector(".ui-flow-node__body")
      if (body) {
        // updateNode body is TEXT: the trusted rich-body contract is
        // server-render only (NodeComponent escapes plain strings; only the
        // caller's .html_safe markup passes raw). A JS data-API sink has no
        // per-string trust marker, so it must escape — same rule as
        // _renderNodeDom's runtime defs.
        if (node.body !== undefined && node.body !== null) body.textContent = `${node.body}`
        else if (label !== undefined && label !== null) body.textContent = `${label}`
      }
      el.setAttribute("aria-label", `${label ?? node.id}`)
      // Keep the always-present label value attribute in step (server
      // contract parity — see _renderNodeDom): label patches re-write it,
      // converge and retraction alike.
      el.setAttribute("data-ui--flow-node-label-value", `${label ?? ""}`)
    }
    // Engine class/style route through the node controller's tracked-replace
    // helpers (NODE-REM) — a retracted preset actually leaves; host
    // classes/properties are never touched.
    if (attrs.style !== undefined) this._syncNodeEngineStyle(el, attrs.style)
    if (attrs.class !== undefined) this._syncNodeEngineClass(el, attrs.class)
    if (attrs.width !== undefined) { el.setAttribute("data-ui--flow-node-width-value", `${attrs.width}`); if (attrs.width) el.style.width = `${attrs.width}px` }
    if (attrs.height !== undefined) { el.setAttribute("data-ui--flow-node-height-value", `${attrs.height}`); if (attrs.height) el.style.height = `${attrs.height}px` }
    if (attrs.draggable !== undefined) el.setAttribute("data-ui--flow-node-draggable-value", `${attrs.draggable}`)
    if (attrs.selectable !== undefined) el.setAttribute("data-ui--flow-node-selectable-value", `${attrs.selectable}`)
    if (attrs.hidden !== undefined) this._syncNodeHidden(el, attrs.hidden)
    // Collapse (F4) is a plain engine class — not the NODE-REM tracked-replace
    // slot (a class write must not retract status chrome) and not the node
    // controller's hide/show contract (the node stays visible + hit-testable).
    if (attrs.collapsed !== undefined) el.classList.toggle("ui-flow-node--collapsed", !!attrs.collapsed)
    // U3 independent channels: each tracks its own engine-owned set and
    // retracts on its own — a class write never wipes status/annotation/badge
    // chrome and vice versa.
    if (attrs.status !== undefined) this._syncNodeStatus(el, attrs.status)
    if (attrs.annotations !== undefined) this._syncNodeAnnotations(el, attrs.annotations)
    if (attrs.badges !== undefined) this._syncNodeBadges(el, attrs.badges)
  }

  // Hidden toggling routes through the node controller's hide()/show() contract
  // when the controller is mounted (its value change re-syncs the attribute);
  // the fallback handles markup Stimulus has not upgraded (tests, pre-upgrade).
  _syncNodeHidden(el, hidden) {
    let controller = null
    try { controller = this.application?.getControllerForElementAndIdentifier(el, "ui--flow-node") || null } catch { controller = null }
    if (controller) { hidden ? controller.hide() : controller.show(); return }
    el.setAttribute("data-ui--flow-node-hidden-value", `${hidden}`)
    el.style.display = hidden ? "none" : ""
  }

  // Engine class routing (NODE-REM): the node controller's tracked replace is
  // preferred when mounted; the fallback replicates the semantics for markup
  // Stimulus has not upgraded (same routing pattern as _syncNodeHidden).
  _syncNodeEngineClass(el, classStr) {
    let controller = null
    try { controller = this.application?.getControllerForElementAndIdentifier(el, "ui--flow-node") || null } catch { controller = null }
    if (controller) { controller.setEngineClasses(classStr); return }
    ;(el.dataset.flowEngineClasses || "").split(/\s+/).filter(Boolean).forEach(c => el.classList.remove(c))
    const next = `${classStr ?? ""}`.split(/\s+/).filter(Boolean)
    next.forEach(c => el.classList.add(c))
    if (next.length) el.dataset.flowEngineClasses = next.join(" ")
    else delete el.dataset.flowEngineClasses
  }

  // Engine style routing (NODE-REM): string form replaces the engine's
  // tracked properties (probe div, as updateEdge does); object form assigns
  // per key.
  _syncNodeEngineStyle(el, style) {
    let controller = null
    try { controller = this.application?.getControllerForElementAndIdentifier(el, "ui--flow-node") || null } catch { controller = null }
    if (controller) { controller.setEngineStyle(style); return }
    if (typeof style === "string") {
      ;(el.dataset.flowEngineStyleProps || "").split(/\s+/).filter(Boolean).forEach(p => el.style.removeProperty(p))
      const probe = document.createElement("div")
      probe.style.cssText = style
      const props = []
      for (let i = 0; i < probe.style.length; i++) props.push(probe.style[i])
      props.forEach(p => el.style.setProperty(p, probe.style.getPropertyValue(p)))
      if (props.length) el.dataset.flowEngineStyleProps = props.join(" ")
      else delete el.dataset.flowEngineStyleProps
    } else if (style && typeof style === "object") Object.assign(el.style, style)
  }

  // ── U3 independent node channels ────────────────────────
  // Status, annotations and badges are three SEPARATE engine-owned channels
  // on the node root, each tracked in its own dataset key and retractable on
  // its own — deliberately NOT the NODE-REM class slot: a host preset or
  // updateNode({class}) write must never wipe status chrome (and a status
  // change must never retract a host class). Class-name material is
  // whitespace-collapsed to "-" so a caller passing an unexpected shape can
  // never make classList.add throw (a space is an invalid class token).
  _syncNodeStatus(el, status) {
    // Retract exactly the previously projected class, tracked per element.
    const previous = el.dataset.flowEngineStatusClass
    if (previous) { el.classList.remove(previous); delete el.dataset.flowEngineStatusClass }
    if (status === undefined || status === null || `${status}`.trim() === "") return
    const className = `ui-flow-node--status-${`${status}`.trim().replace(/\s+/g, "-")}`
    el.classList.add(className)
    el.dataset.flowEngineStatusClass = className
  }

  _syncNodeAnnotations(el, annotations) {
    // Diff-replace: drop the previous annotation set (tracked suffixes), add
    // the next. Non-Array clears — annotations: null is the "clear" write.
    ;(el.dataset.flowEngineAnnotations || "").split(/\s+/).filter(Boolean).forEach(name => el.classList.remove(`ui-flow-node--annotation-${name}`))
    const next = Array.isArray(annotations)
      ? annotations.filter(a => a !== null && a !== undefined && `${a}`.trim() !== "").map(a => `${a}`.trim().replace(/\s+/g, "-"))
      : []
    next.forEach(name => el.classList.add(`ui-flow-node--annotation-${name}`))
    if (next.length) el.dataset.flowEngineAnnotations = next.join(" ")
    else delete el.dataset.flowEngineAnnotations
  }

  // Badge strip: engine-owned (marked data-flow-engine-badges), rebuilt on
  // every write, text via textContent (untrusted sink). Shape: { key: label }
  // renders scalar labels in the neutral tone; { key: { label, tone } } picks
  // the tone. Non-object (null/undefined/Array) removes the strip. The strip
  // anchors before the first handle (NodeComponent renders the authored badge
  // slot in the same position); with no handles it lands last.
  _syncNodeBadges(el, badges) {
    el.querySelector(".ui-flow-node__badges[data-flow-engine-badges]")?.remove()
    if (!badges || typeof badges !== "object" || Array.isArray(badges)) return
    const TONES = ["neutral", "primary", "success", "warning", "danger", "info", "outline"]
    const strip = document.createElement("div")
    strip.setAttribute("class", "ui-flow-node__badges")
    strip.setAttribute("data-flow-engine-badges", "")
    Object.keys(badges).forEach(key => {
      const value = badges[key]
      const entry = value && typeof value === "object" && !Array.isArray(value) ? value : { label: value }
      if (entry.label === undefined || entry.label === null || `${entry.label}` === "") return
      const span = document.createElement("span")
      const tone = TONES.includes(entry.tone) ? entry.tone : "neutral"
      span.setAttribute("class", `ui-flow-node__badge ui-flow-node__badge--${tone}`)
      span.setAttribute("data-badge-key", `${key}`)
      span.textContent = `${entry.label}`
      strip.appendChild(span)
    })
    if (!strip.firstChild) return
    const firstHandle = el.querySelector(".ui-flow-handle")
    if (firstHandle) firstHandle.before(strip)
    else el.appendChild(strip)
  }

  // ── Undo/Redo ───────────────────────────────────────────

  _snapshot() {
    return JSON.parse(JSON.stringify({ nodes: this.getNodes(), edges: this.getEdges() }))
  }

  // `presnapshot` is for gestures that captured the pre-mutation state at
  // gesture START (endNodeDrag): the guards and trim must apply to that push
  // exactly like a fresh snapshot (minor #6 — a drag inside a batch must not
  // add a second entry on top of the batch's own).
  _pushUndo(presnapshot = null) {
    // Restoring a snapshot (undo/redo/fromObject) drives removeNode/removeEdge,
    // which push undo of their own — without this guard, undo would corrupt the
    // very stacks it is restoring from. The batch flag is the public cousin:
    // mutations inside beginBatch/endBatch are covered by the batch's single
    // snapshot, so their own pushes are suppressed too.
    if (this._applyingSnapshot || this._batchDepth > 0) return
    this._undoStack.push(presnapshot || this._snapshot())
    this._redoStack = []
    if (this._undoStack.length > this._maxHistory) this._undoStack.shift()
  }

  // Batch undo: hosts stitching several mutations into ONE undo entry (the
  // delete_middle_node pattern — removeNode + the stitch addEdge) bracket
  // them in beginBatch/endBatch. Snapshots are PRE-state, so beginBatch
  // records the pre-batch graph once and every mutation inside skips its
  // own _pushUndo; the next undo restores the whole batch. Batches nest
  // (only the outermost begin records). An empty batch drops its snapshot
  // again — undo must never apply a state identical to the current one.
  beginBatch() {
    if (this._batchDepth === 0) {
      this._undoStack.push(this._snapshot())
      this._redoStack = []
      if (this._undoStack.length > this._maxHistory) this._undoStack.shift()
    }
    this._batchDepth++
  }

  endBatch() {
    if (this._batchDepth === 0) return
    this._batchDepth--
    if (this._batchDepth === 0 && this._undoStack.length > 0) {
      const top = this._undoStack[this._undoStack.length - 1]
      if (JSON.stringify(top) === JSON.stringify(this._snapshot())) this._undoStack.pop()
    }
  }

  undo() {
    if (this._undoStack.length === 0) return
    const current = this._snapshot()
    this._redoStack.push(current)
    const prev = this._undoStack.pop()
    this._applySnapshot(prev)
    this._emit("undo", { snapshot: prev })
  }

  redo() {
    if (this._redoStack.length === 0) return
    const current = this._snapshot()
    this._undoStack.push(current)
    const next = this._redoStack.pop()
    this._applySnapshot(next)
    this._emit("redo", { snapshot: next })
  }

  _applySnapshot(snapshot) {
    // Remove current nodes/edges that aren't in snapshot
    const snapNodeIds = new Set(snapshot.nodes.map(n => n.id))
    const snapEdgeIds = new Set(snapshot.edges.map(e => e.id))
    this._applyingSnapshot = true
    try {
      this._nodes.forEach((_, id) => { if (!snapNodeIds.has(id)) this.removeNode(id) })
      this._edges.forEach((_, id) => { if (!snapEdgeIds.has(id)) this.removeEdge(id) })

      // Add/update nodes from snapshot — PARENTS BEFORE CHILDREN (N7):
      // _renderNodeDom nests a child only into an already-rendered parent, so
      // a payload listing a child first would strand it at the canvas root
      // with parent-relative coordinates. Depth is computed within the
      // payload (a parent missing from the payload — already in the DOM or
      // orphaned — contributes 0); the guard bounds parentId cycles. The sort
      // is stable, so same-depth nodes keep payload order.
      const snapById = new Map(snapshot.nodes.map(n => [n.id, n]))
      const depthOf = (nDef) => {
        let depth = 0, cur = nDef, guard = 0
        while (cur.parentId && snapById.has(cur.parentId) && guard++ <= snapshot.nodes.length) {
          cur = snapById.get(cur.parentId)
          depth++
        }
        return depth
      }
      ;[...snapshot.nodes].sort((a, b) => depthOf(a) - depthOf(b)).forEach(nDef => {
        const existing = this._nodes.get(nDef.id)
        if (existing) {
          // Position-less defs (minor #2): converge the rest, skip the move —
          // reading nDef.position.x/y blind would TypeError and abort the
          // whole restore mid-way.
          // Position handling FIRST, unchanged (replaceNode never participates
          // in position — structural, moveNode's op domain). Presentation then
          // converges EXACTLY through replaceNode — edge-parity with the B1
          // branch below (this closes the undo-relabel hole the merge shaped
          // before converge: the old no-key-drop Object.assign rewrote the
          // local map+DOM but emitted NO nodechange, so undo of a relabel/
          // reclass/restyle left peers holding the pre-undo def — keys
          // retracted on the originator survived on peers. replaceNode emits
          // one full-def nodechange per CHANGED node; the sync channel
          // forwards it as the updateNode op).
          const hasPosition = nDef.position && nDef.position.x != null && nDef.position.y != null
          const el = this._nodeElement(nDef.id)
          if (el && hasPosition) {
            // Existing-node position restore routes through _moveNode (M1) —
            // the SAME path a drag takes: style.left/top AND the x/y value
            // attributes stay in lockstep (node_controller.pointerDown derives
            // co-selected drag offsets from those attributes, so a style-only
            // write teleports the next group drag by the stale delta), and the
            // nodemove emission re-anchors connected edges. _moveNode pushes no
            // undo of its own and the _applyingSnapshot flag guards the push
            // sites reachable from here, so the restore stays self-contained.
            // Snap/extent re-clamping is idempotent — snapshot positions were
            // clamped when stored.
            // Only when the rendered position actually differs: nodemove is a
            // sync-channel moveNode broadcast, and a no-op restore (undo of an
            // ADD — the surviving siblings never moved) must not spam peers
            // with positions they already have.
            // An ABSENT value attribute is not staleness (bare markup has no
            // Stimulus values to keep in lockstep) — only an existing one
            // that disagrees is.
            const xAttr = el.getAttribute("data-ui--flow-node-x-value")
            const yAttr = el.getAttribute("data-ui--flow-node-y-value")
            const stale = el.style.left !== `${nDef.position.x}px` || el.style.top !== `${nDef.position.y}px` ||
              (xAttr !== null && xAttr !== `${nDef.position.x}`) || (yAttr !== null && yAttr !== `${nDef.position.y}`)
            if (stale) this._moveNode(nDef.id, nDef.position.x, nDef.position.y)
          }
          // No-op-restore skip (edge parity): a def equal minus the position
          // run above emits no nodechange — every nodechange is a sync-channel
          // updateNode op to peers, and undo of a pure move must broadcast
          // only its one moveNode. Equal-after-strip implies any dropped key
          // carried an undefined value — its DOM projection is already the
          // default — so skipping retraction for it loses nothing; a REAL
          // dropped key makes the defs differ and replaceNode's retraction
          // still runs.
          if (this._nodeDefsEqual(existing, nDef)) return
          this.replaceNode(nDef)
        } else {
          if (!nDef.handles) nDef.handles = this._defaultHandlesFor(nDef.id, nDef.type)
          this._nodes.set(nDef.id, nDef)
          this._renderNodeDom(nDef)
          this._emit("nodesadd", { nodes: [nDef] })
        }
      })

      // Add/update edges from snapshot (B1): an edge whose id ALREADY exists —
      // undo of a reconnect, or a fromObject Load over live ids — must be
      // restored too, not skipped: replaceEdge converges the map entry to
      // EXACTLY the snapshot def (stale keys dropped — nothing survives that
      // the snapshot doesn't carry — and their DOM projections retracted)
      // and re-syncs through updateEdge's attribute projection + edge
      // refresh(), so the identity attributes and `d` match the snapshot
      // exactly. Mirror of the node branch's replaceNode converge (both
      // entity kinds follow ONE rule on the same restore path) — no-spam
      // rule included: an edge the snapshot carries UNCHANGED is skipped
      // without replaceEdge, because every edgechange emission is a
      // sync-channel op to peers (undo of a node move must not broadcast E
      // no-op updateEdge ops alongside the one moveNode).
      snapshot.edges.forEach(eDef => {
        if (this._edges.has(eDef.id)) {
          const existing = this._edges.get(eDef.id)
          // No-op-restore skip (node-branch parity): a def deep-equal to the
          // live entry needs no restore, and the edgechange replaceEdge emits
          // would broadcast a no-change op to every sync peer. Equal defs
          // imply any dropped key carried an undefined value — its DOM
          // projection is already the default — so skipping retraction for it
          // loses nothing; a REAL dropped key makes the defs differ and
          // replaceEdge's retraction still runs.
          if (this._edgeDefsEqual(existing, eDef)) return
          this.replaceEdge(eDef)
        } else {
          this._edges.set(eDef.id, eDef)
          this._renderEdgeDom(eDef)
          this._emit("edgesadd", { edges: [eDef] })
        }
      })

      this._emit("restore", snapshot)
      this._syncEmptyState()
    } finally {
      this._applyingSnapshot = false
    }
  }

  get canUndo() { return this._undoStack.length > 0 }
  get canRedo() { return this._redoStack.length > 0 }

  // ── Copy/Paste ──────────────────────────────────────────

  // BP#10 (Cmd/Ctrl+D): duplicate the selection THROUGH the clipboard path —
  // one undo entry, +30/+30 offset, remapped handle ids, internal edges
  // re-pointed — so the duplicate shape can never drift from copy/paste.
  _duplicateSelection() {
    if (this.selectedNodes.size === 0) return
    this.copySelection()
    this.pasteClipboard()
  }

  copySelection() {
    if (this.selectedNodes.size === 0) return
    this._clipboard = []
    const selectedIds = new Set(this.selectedNodes)
    // Copy selected nodes
    selectedIds.forEach(id => {
      const node = this._nodes.get(id)
      if (node) this._clipboard.push({ ...node, position: { ...node.position }, data: JSON.parse(JSON.stringify(node.data || {})) })
    })
    // Copy edges between selected nodes
    this._edges.forEach(e => {
      if (selectedIds.has(e.source) && selectedIds.has(e.target)) {
        this._clipboard.push({ ...e, __isEdge: true })
      }
    })
    this._emit("copy", { items: this._clipboard })
  }

  pasteClipboard() {
    // Canvas-wide gate (U2): read-only blocks paste (and with it Cmd+D
    // duplicate, which routes through the clipboard path).
    if (!this.interactive) return
    if (this._clipboard.length === 0) return
    const undoDepth = this._undoStack.length
    this._pushUndo()
    const offset = 30
    const idMap = new Map()
    const newNodes = []
    const newEdges = []

    this._clipboard.forEach(item => {
      if (item.__isEdge) return
      // U7: the paste path goes through the id-factory hook too. A canceled
      // node is skipped entirely — its internal edges drop out naturally
      // below (idMap never records the source/target). A host-rewritten id
      // re-points the handle remap, which embeds the node id; a source def
      // without handle info (e.g. a fromObject payload) falls back to the
      // per-type defaults.
      const proposedId = `${item.id}-copy-${Date.now()}-${this._nodeSeq++}`
      const nodeDef = {
        ...item,
        id: proposedId,
        position: { x: item.position.x + offset, y: item.position.y + offset }
      }
      const finalId = this._assignNodeId(nodeDef, "paste")
      if (finalId === null) return
      nodeDef.handles = item.handles ? this._remapHandles(item.handles, item.id, finalId) : this._defaultHandlesFor(finalId, item.type)
      idMap.set(item.id, finalId)
      this._nodes.set(finalId, nodeDef)
      this._renderNodeDom(nodeDef)
      newNodes.push(nodeDef)
    })

    this._clipboard.forEach(item => {
      if (!item.__isEdge) return
      const newSource = idMap.get(item.source)
      const newTarget = idMap.get(item.target)
      if (!newSource || !newTarget) return
      // Same collision suffix the node ids above get (minor #5): two pastes
      // inside one millisecond must not overwrite each other's edge.
      const newId = `${item.id}-copy-${Date.now()}-${this._edgeSeq++}`
      const edgeDef = { ...item, id: newId, source: newSource, target: newTarget }
      delete edgeDef.__isEdge
      this._edges.set(newId, edgeDef)
      this._renderEdgeDom(edgeDef)
      newEdges.push(edgeDef)
    })

    if (newNodes.length) this._emit("nodesadd", { nodes: newNodes })
    if (newEdges.length) this._emit("edgesadd", { edges: newEdges })
    // U7 cancel symmetry with _createNodeFromDetail: a paste where the host
    // canceled EVERY node (and no edges survived) is a no-op — drop its undo
    // entry instead of wasting a history slot. The depth check proves
    // ownership: inside a batch (or a snapshot restore) the push was
    // suppressed, the stack never grew, and the batch's own entry stays.
    if (!newNodes.length && !newEdges.length && this._undoStack.length > undoDepth) this._undoStack.pop()
    this._emit("paste", { nodes: newNodes, edges: newEdges })
  }

  // ── Drop → node creation (G2) ───────────────────────────
  // The dropzone (on the pane, inside this element) converts the pointer to
  // flow coords and dispatches ui--flow:drop; the dnd palette's keyboard path
  // and the context menu's add-here dispatch ui--flow:addnode with the same
  // {type, label, x, y} shape. The ENGINE owns node construction (unique id,
  // per-type default handles, grid snap) so a palette drop lands as a real,
  // connectable node. Both events keep bubbling UNMODIFIED — hosts keep the
  // React Flow onDrop ownership and read the created node off the `nodesadd`
  // event addNode emits synchronously (detail mutation was rejected: it
  // rewrites payloads the upstream-parity tests pin by exact equality).

  _handleDropEvent(event) {
    this._createNodeFromDetail(event.detail || {}, "drop")
  }

  _handleMenuAddNode(event) {
    this._createNodeFromDetail(event.detail || {}, "addnode")
  }

  _createNodeFromDetail(detail, kind = "drop") {
    // Canvas-wide gate (U2): read-only blocks the engine-owned creation
    // gestures (palette drop, context-menu add-here). The host's own onDrop
    // listener still receives the raw ui--flow:drop/ui--flow:addnode event —
    // only the node construction is gated.
    if (!this.interactive) return null
    const type = detail.type || "default"
    let x = detail.x ?? 0
    let y = detail.y ?? 0
    if (this.snapToGridValue) {
      const [gx, gy] = this.snapGridValue
      x = Math.round(x / gx) * gx
      y = Math.round(y / gy) * gy
    }
    const id = `node-${Date.now()}-${this._nodeSeq++}`
    const label = detail.label ?? null
    // React Flow data shape: the label lives under data.
    const nodeDef = { id, type, position: { x, y }, data: label != null ? { label } : {} }
    // An engine-owned creation gesture is undoable — same contract
    // pasteClipboard established. Programmatic addNode stays undo-free
    // (remote sync ops must not push history). A canceled add (U7 hook)
    // drops the just-pushed entry again — it would restore a state identical
    // to the current one (the depth check proves the entry is ours; a
    // batch/snapshot-suppressed push never grows the stack).
    const undoDepth = this._undoStack.length
    this._pushUndo()
    if (this.addNode(nodeDef, kind) === null) {
      if (this._undoStack.length > undoDepth) this._undoStack.pop()
      return null
    }
    this._announce("added", { label: label || nodeDef.id })
    return nodeDef
  }

  // ── Context-menu actions (G3) ───────────────────────────
  // The shipped menu's Delete/Duplicate items dispatch ui--flow:delete /
  // ui--flow:duplicate with { nodeId }. delete cascades the node's anchored
  // edges (one undo entry, deletable enforcement); duplicate copies the node
  // at a +20,+20 offset with remapped handle ids. Custom menu items are
  // host-owned: give the item `click->ui--flow-context-menu#emit` plus a
  // data-event-name attribute and the menu controller dispatches
  // ui--flow:<name> on this element with { nodeId, flowPosition }.

  _handleMenuDelete(event) {
    const nodeId = event.detail?.nodeId
    if (!nodeId) return
    this._deleteNodeWithEdges(nodeId)
  }

  _handleMenuDuplicate(event) {
    const nodeId = event.detail?.nodeId
    const source = nodeId ? this._nodes.get(nodeId) : null
    if (!source) return
    // Canvas-wide gate (U2): read-only blocks duplicate too (the keyboard
    // Cmd+D path is already gated through pasteClipboard).
    if (!this.interactive) return
    const id = `${nodeId}-copy-${Date.now()}-${this._nodeSeq++}`
    const nodeDef = {
      ...source,
      id,
      position: { x: (source.position?.x ?? 0) + 20, y: (source.position?.y ?? 0) + 20 },
      data: JSON.parse(JSON.stringify(source.data || {})),
      handles: this._remapHandles(source.handles, nodeId, id)
    }
    const undoDepth = this._undoStack.length
    this._pushUndo()
    if (this.addNode(nodeDef, "duplicate") === null) {
      if (this._undoStack.length > undoDepth) this._undoStack.pop()
      return
    }
    this._announce("duplicated", { label: source.data?.label || nodeId })
  }

  // Menu delete of one node runs the same cascade _deleteSelected runs: the
  // node plus EVERY edge anchored to it, as ONE undo entry. removeNode and
  // removeEdge each push undo of their own, so after pushing once here the
  // composite runs under the snapshot guard (the flag suppresses inner
  // pushes — the same self-containment _applySnapshot relies on).
  _deleteNodeWithEdges(nodeId) {
    // Canvas-wide gate (U2): read-only blocks the context-menu delete.
    if (!this.interactive) return
    const el = this._nodeElement(nodeId)
    if (!this._nodes.has(nodeId) && !el) return
    if (el && el.dataset["ui-FlowNodeDeletableValue"] === "false") {
      this._emit("nodedeleteblocked", { nodeId })
      return
    }
    this._pushUndo()
    let removed = 1
    this._applyingSnapshot = true
    try {
      this._edges.forEach((edge, id) => {
        if (edge.source === nodeId || edge.target === nodeId) { this.removeEdge(id); removed++ }
      })
      this.removeNode(nodeId)
    } finally {
      this._applyingSnapshot = false
    }
    this._announce("deleted", { count: removed, countPlural: removed === 1 ? "" : "s" })
  }

  // ── Smooth Viewport Animations ─────────────────────────

  setViewportAnimated(vp, duration = 300) {
    // Reduced motion (complement to the CSS side): skip the rAF easing and
    // jump straight to the target viewport.
    if (typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.setViewport(vp)
      return
    }
    if (this._animRaf) cancelAnimationFrame(this._animRaf)
    const start = { ...this.viewport }
    const startTime = performance.now()
    const animate = (now) => {
      const t = Math.min((now - startTime) / duration, 1)
      const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2 // easeInOutQuad
      this.viewport.x = start.x + (vp.x - start.x) * ease
      this.viewport.y = start.y + (vp.y - start.y) * ease
      this.viewport.zoom = start.zoom + ((vp.zoom ?? start.zoom) - start.zoom) * ease
      this._applyTransform()
      this._emitViewportChange()
      if (t < 1) this._animRaf = requestAnimationFrame(animate)
      else this._animRaf = null
    }
    this._animRaf = requestAnimationFrame(animate)
  }

  zoomAtAnimated(delta, point, duration = 300) {
    const oldZoom = this.viewport.zoom
    let newZoom = Math.max(this.minZoomValue, Math.min(this.maxZoomValue, oldZoom * (1 + delta)))
    const ratio = newZoom / oldZoom
    const newX = point.x - (point.x - this.viewport.x) * ratio
    const newY = point.y - (point.y - this.viewport.y) * ratio
    this.setViewportAnimated({ x: newX, y: newY, zoom: newZoom }, duration)
  }

  fitViewAnimated(padding = 50, duration = 300) {
    if (!this.hasNodesTarget) return
    // Same bounds contract as fitView: hidden nodes (the shared
    // _nodeVisible predicate) contribute nothing.
    const nodes = [...this.nodesTarget.querySelectorAll("[data-ui--flow-node]")].filter(n => this._nodeVisible(n))
    if (nodes.length === 0) { this.setViewportAnimated({ x: 0, y: 0, zoom: 1 }, duration); return }
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    nodes.forEach(n => { const p = this.flowPosition(n); minX = Math.min(minX, p.x); minY = Math.min(minY, p.y); maxX = Math.max(maxX, p.x + n.offsetWidth); maxY = Math.max(maxY, p.y + n.offsetHeight) })
    const rect = this.element.getBoundingClientRect(), cw = maxX - minX + padding * 2, ch = maxY - minY + padding * 2
    const zoom = Math.min(rect.width / cw, rect.height / ch, this.maxZoomValue)
    const x = (rect.width - cw * zoom) / 2 - minX * zoom + padding * zoom
    const y = (rect.height - ch * zoom) / 2 - minY * zoom + padding * zoom
    this.setViewportAnimated({ x, y, zoom }, duration)
  }

  // ── Helpers ─────────────────────────────────────────────

  _applyTransform() {
    if (this.hasPaneTarget) this.paneTarget.style.transform = `translate(${this.viewport.x}px, ${this.viewport.y}px) scale(${this.viewport.zoom})`
    // Zoom as CSS custom properties (UX audit): chrome living INSIDE the
    // transformed pane (the node toolbar) counter-scales with
    // var(--ui-flow-zoom-inv) so it renders at a constant screen size — a CSS
    // read, so no viewportchange listener is needed per widget.
    this.element.style.setProperty("--ui-flow-zoom", `${this.viewport.zoom}`)
    this.element.style.setProperty("--ui-flow-zoom-inv", `${1 / this.viewport.zoom}`)
  }
  _emitViewportChange() { this._applyZoomBand(); this._emit("viewportchange", { viewport: { ...this.viewport } }) }

  // BP#9 zoom-band LOD: one quantized class on the canvas root per zoom band —
  // `ui-flow--zoom-far` (< 0.4) and `ui-flow--zoom-mid` (< 0.8); the near band
  // (>= 0.8) is the unmarked default (full detail, no overrides). The swap
  // touches classList only when the band CHANGES: animated zooms emit
  // viewportchange per frame, and a per-frame classList write would thrash
  // style recalc. flow.css hangs the far-band rendering cuts off the class;
  // hosts can hook either band for their own LOD (the pattern the
  // server_monitoring scenario hand-rolled with `rf-zoomed-out`).
  _applyZoomBand() {
    const zoom = this.viewport.zoom
    const band = zoom < 0.4 ? "ui-flow--zoom-far" : zoom < 0.8 ? "ui-flow--zoom-mid" : null
    if (band === this._zoomBand) return
    if (this._zoomBand) this.element.classList.remove(this._zoomBand)
    if (band) this.element.classList.add(band)
    this._zoomBand = band
  }
  get zoomPercentage() { return Math.round(this.viewport.zoom * 100) }
}
